import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Users, UserPlus, Search, Building2, Briefcase, ChevronLeft, ChevronRight, Edit, Trash2, ClipboardList, X, ArrowLeft, Phone, Mail, Calendar, Check, ArrowUpDown, Download, Network, FileText, CalendarX, ListTodo, Plane, Upload, User, BarChart3, Award, ChevronDown, ChevronRight as ChevronRightIcon, Shield, MessageSquare, Bell, Megaphone, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import apiClient from '../api/client';
import {
  DEMO_DEPARTMENTS,
  DEMO_POSITIONS,
  DEMO_PEOPLE,
  getDemoPeopleList,
  getDemoAttendance,
  DEMO_ANALYTICS,
  DEMO_REVIEW_CYCLES,
  getDemoReviewsForCycle,
  DEMO_UPCOMING_LEAVE,
  DEMO_ANNOUNCEMENTS,
  DEMO_HOLIDAYS,
  DEMO_BENEFIT_PLANS,
  DEMO_PTO_POLICIES,
  DEMO_AUDIT_ITEMS,
} from '../data/hrDemoData';

/* ── Constants & Helpers ─────────────────────────────── */
const STATUS_CLS = { active: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', on_leave: 'bg-amber-50 text-amber-700 ring-amber-600/20', offboarded: 'bg-red-50 text-red-700 ring-red-600/20' };
const ATTENDANCE_CLS = { present: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', leave: 'bg-amber-50 text-amber-700 ring-amber-600/20', absent: 'bg-red-50 text-red-700 ring-red-600/20', late: 'bg-amber-50 text-amber-600 ring-amber-600/20', wfh: 'bg-blue-50 text-blue-700 ring-blue-600/20', business_trip: 'bg-violet-50 text-violet-700 ring-violet-600/20' };
const STATUS_KEYS = { active: 'statusActive', on_leave: 'statusOnLeave', offboarded: 'statusOffboarded' };
const ATTENDANCE_KEYS = { present: 'attPresent', leave: 'attLeave', absent: 'attAbsent', late: 'attLate', wfh: 'attWfh', business_trip: 'attBusinessTrip' };
const TYPE_KEYS = { full_time: 'typeFullTime', part_time: 'typePartTime', volunteer: 'typeVolunteer', intern: 'typeIntern', contractor: 'typeContractor' };
const statusCls = (s) => STATUS_CLS[s] || 'bg-gray-50 text-gray-600 ring-gray-500/10';
const getStatusLabel = (t) => (s) => t('hr.' + (STATUS_KEYS[s] || s));
const getTypeLabel = (t) => (typ) => t('hr.' + (TYPE_KEYS[typ] || typ));
const getAttLabel = (t) => (s) => t('hr.' + (ATTENDANCE_KEYS[s] || s));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : null;

function useDebounce(value, ms = 350) {
  const [d, setD] = useState(value);
  useEffect(() => { const t = setTimeout(() => setD(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return d;
}

const Avatar = ({ name, size = 'md', photoUrl }) => {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const palette = ['#3B82F6', '#10B981', '#8B5CF6', '#F43F5E', '#F59E0B', '#06B6D4', '#EC4899', '#6366F1'];
  const bg = palette[Math.abs([...(name || '')].reduce((a, c) => a + c.charCodeAt(0), 0)) % palette.length];
  const s = { sm: 'h-8 w-8 text-[11px]', md: 'h-10 w-10 text-xs', lg: 'h-14 w-14 text-base' }[size] || 'h-10 w-10 text-xs';
  if (photoUrl) {
    return <img src={photoUrl} alt="" className={`${s} rounded-full object-cover shrink-0 shadow-sm`} />;
  }
  return <div className={`${s} rounded-full flex items-center justify-center text-white font-semibold shrink-0 shadow-sm`} style={{ backgroundColor: bg }}>{initials}</div>;
};

const Badge = ({ children, className = '' }) => <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-md ring-1 ring-inset ${className}`}>{children}</span>;

const Toast = ({ message, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 2500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className="fixed bottom-6 right-6 z-[60] flex items-center gap-2 bg-[#37352F] text-white pl-3 pr-4 py-2.5 rounded-lg shadow-lg text-sm animate-fade-in">
      <Check className="h-4 w-4 text-emerald-400" />{message}
    </div>
  );
};

const getLeaveTypes = (t) => ({ vacation: t('hr.leaveVacation'), sick: t('hr.leaveSick'), personal: t('hr.leavePersonal'), other: t('hr.leaveOther') });

/* ── CSV Export helper ────────────────────────────────── */
function exportCSV(people, departments, t, statusLabel, typeLabel) {
  const headers = [t('hr.name'), t('hr.email'), t('hr.firstName'), t('hr.lastName'), t('hr.department'), t('hr.position'), t('hr.employmentType'), t('hr.status'), t('hr.phone'), t('hr.startDate'), t('hr.endDate'), t('hr.manager'), t('hr.notes')];
  const escape = (v) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s; };
  const effectiveStatus = (p) => (p.today_attendance_status === 'leave' ? 'on_leave' : p.status);
  const rows = people.map(p => [p.display_name, p.email, p.first_name, p.last_name, p.department_name, p.position_title, typeLabel(p.employment_type), statusLabel(effectiveStatus(p)), p.phone, p.start_date, p.end_date, p.manager_name, p.notes].map(escape).join(','));
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `people-export-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ── Main ─────────────────────────────────────────────── */
const HR = () => {
  const { t } = useLanguage();
  const statusLabel = getStatusLabel(t);
  const typeLabel = getTypeLabel(t);
  const attLabel = getAttLabel(t);
  const [view, setView] = useState('overview');
  const [activeDeptId, setActiveDeptId] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [toast, setToast] = useState(null);

  // Data
  const [people, setPeople] = useState([]);
  const [totalPeople, setTotalPeople] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sort, setSort] = useState('name_asc');
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [auditItems, setAuditItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // All people (for manager picker & org chart — fetched once, no filters)
  const [allPeople, setAllPeople] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [reviewCycles, setReviewCycles] = useState([]);
  const [benefitPlans, setBenefitPlans] = useState([]);
  const [upcomingLeave, setUpcomingLeave] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [myTeam, setMyTeam] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [ptoPolicies, setPtoPolicies] = useState([]);
  const [accrualResult, setAccrualResult] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [useDemoData, setUseDemoData] = useState(false);
  const [attendanceList, setAttendanceList] = useState([]);
  const [attendanceDate, setAttendanceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [punchLoading, setPunchLoading] = useState(false);

  // Fetch base data; if API returns no data, use demo data so HR can still explore the page
  useEffect(() => {
    Promise.all([
      apiClient.getDepartments(false).catch(() => []),
      apiClient.getPositions(false).catch(() => []),
      apiClient.getPeople({ limit: 100 }).catch(() => ({ items: [] })),
    ]).then(([d, p, ppl]) => {
      const depts = Array.isArray(d) ? d : [];
      const pos = Array.isArray(p) ? p : [];
      const peopleList = ppl?.items ?? [];
      if (depts.length === 0 && peopleList.length === 0) {
        setUseDemoData(true);
        setDepartments(DEMO_DEPARTMENTS);
        setPositions(DEMO_POSITIONS);
        setAllPeople(DEMO_PEOPLE);
      } else {
        setDepartments(depts);
        setPositions(pos);
        setAllPeople(peopleList);
      }
    });
  }, []);

  // Fetch people (filtered view); use demo list when backend has no data
  const fetchPeople = useCallback(async () => {
    setLoading(true);
    if (useDemoData) {
      const res = getDemoPeopleList({ page, limit: 20, search: search || undefined, department_id: activeDeptId || undefined, status: statusFilter || undefined, employment_type: typeFilter || undefined, sort });
      setPeople(res.items); setTotalPeople(res.total); setPages(res.pages);
      setLoading(false);
      return;
    }
    try {
      const res = await apiClient.getPeople({ page, limit: 20, search: search || undefined, department_id: activeDeptId || undefined, status: statusFilter || undefined, employment_type: typeFilter || undefined, sort });
      setPeople(res.items || []); setTotalPeople(res.total || 0); setPages(res.pages || 1);
    } catch { setPeople([]); setTotalPeople(0); setPages(1); }
    setLoading(false);
  }, [useDemoData, page, search, activeDeptId, statusFilter, typeFilter, sort]);

  useEffect(() => { fetchPeople(); }, [fetchPeople]);
  useEffect(() => { setPage(1); }, [search, activeDeptId, statusFilter, typeFilter, sort]);
  useEffect(() => {
    if (view === 'directory' || view === 'benefits') {
      if (useDemoData) setBenefitPlans(DEMO_BENEFIT_PLANS);
      else apiClient.getBenefitPlans(false).then(setBenefitPlans).catch(() => setBenefitPlans([]));
    }
  }, [view, useDemoData]);
  useEffect(() => {
    if (view === 'pto') {
      if (useDemoData) setPtoPolicies(DEMO_PTO_POLICIES);
      else apiClient.getPTOPolicies(false).then(setPtoPolicies).catch(() => setPtoPolicies([]));
    }
  }, [view, useDemoData]);
  useEffect(() => {
    if (view === 'overview') {
      if (useDemoData) {
        setUpcomingLeave(DEMO_UPCOMING_LEAVE);
        setAnnouncements(DEMO_ANNOUNCEMENTS);
        setHolidays(DEMO_HOLIDAYS);
      } else {
        apiClient.getUpcomingLeave().then(setUpcomingLeave).catch(() => setUpcomingLeave([]));
        apiClient.getAnnouncements().then(setAnnouncements).catch(() => setAnnouncements([]));
        const today = new Date();
        const end = new Date(today);
        end.setMonth(end.getMonth() + 3);
        apiClient.getHolidays({ start_date: today.toISOString().slice(0, 10), end_date: end.toISOString().slice(0, 10) }).then(setHolidays).catch(() => setHolidays([]));
      }
      if (localStorage.getItem('auth_token')) {
        apiClient.getMyTeam().then(r => setMyTeam(r.members || [])).catch(() => setMyTeam([]));
        apiClient.getMyNotifications().then(setNotifications).catch(() => setNotifications([]));
      } else {
        setMyTeam([]);
        setNotifications([]);
      }
    }
  }, [view, useDemoData]);

  // Fetch audit on demand
  const fetchAudit = useCallback(async () => {
    if (useDemoData) { setAuditItems(DEMO_AUDIT_ITEMS); return; }
    try { const res = await apiClient.getAuditLog({ page: 1, limit: 50 }); setAuditItems(res.items || []); } catch { setAuditItems([]); }
  }, [useDemoData]);
  useEffect(() => { if (view === 'audit') fetchAudit(); }, [view, fetchAudit]);
  useEffect(() => {
    if (view === 'analytics') {
      if (useDemoData) setAnalytics(DEMO_ANALYTICS);
      else apiClient.getAnalytics().then(setAnalytics).catch(() => setAnalytics(null));
    }
  }, [view, useDemoData]);
  useEffect(() => {
    if (view === 'reviews') {
      if (useDemoData) setReviewCycles(DEMO_REVIEW_CYCLES);
      else apiClient.getReviewCycles().then(setReviewCycles).catch(() => setReviewCycles([]));
    }
  }, [view, useDemoData]);
  useEffect(() => {
    if (view === 'attendance') {
      setAttendanceLoading(true);
      if (useDemoData) {
        setAttendanceList(getDemoAttendance(attendanceDate));
        setAttendanceLoading(false);
      } else {
        apiClient.getAttendance({ date: attendanceDate }).then(setAttendanceList).catch(() => setAttendanceList([])).finally(() => setAttendanceLoading(false));
      }
    }
  }, [view, attendanceDate, useDemoData]);

  // Keyboard
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') { if (modal) setModal(null); else if (selectedPerson) setSelectedPerson(null); } };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [modal, selectedPerson]);

  // Nav helpers
  const goToDept = (id) => { setActiveDeptId(id); setSearchInput(''); setStatusFilter(''); setTypeFilter(''); setView('directory'); };
  const goToDirectory = () => { setActiveDeptId(null); setSearchInput(''); setStatusFilter(''); setTypeFilter(''); setView('directory'); };
  const goToOverview = () => { setActiveDeptId(null); setView('overview'); };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`${t('hr.offboard')} ${name}?`)) return;
    try { await apiClient.deletePerson(id); setSelectedPerson(null); setSelectedIds(s => new Set([...s].filter(x => x !== id))); fetchPeople(); refreshAllPeople(); setToast(`${name} ${t('hr.toastOffboarded')}`); } catch {}
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === people.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(people.map(p => p.id)));
  };
  const handleBulkStatus = async (newStatus) => {
    if (selectedIds.size === 0) return;
    try {
      const res = await apiClient.bulkUpdateStatus([...selectedIds], newStatus);
      setSelectedIds(new Set());
      fetchPeople(); refreshAllPeople();
      setToast(t('hr.toastUpdated', { count: res.updated, status: statusLabel(newStatus) }));
    } catch {}
  };
  const handleExportSelected = () => {
    if (selectedIds.size === 0) return;
    const selected = people.filter(p => selectedIds.has(p.id));
    exportCSV(selected, departments, t, statusLabel, typeLabel);
    setToast(t('hr.toastExported', { count: selected.length }));
  };

  const handleCSVImport = async (file, onDone) => {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) { setToast(t('hr.toastCsvInvalid')); return; }
    const parseLine = (line) => {
      const out = []; let cur = ''; let inQ = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') inQ = !inQ;
        else if (c === ',' && !inQ) { out.push(cur.trim()); cur = ''; }
        else cur += c;
      }
      out.push(cur.trim());
      return out;
    };
    const headers = parseLine(lines[0]).map(h => h.toLowerCase().replace(/"/g, '').trim());
    const rows = lines.slice(1).map(line => headers.reduce((o, h, i) => { o[h] = (parseLine(line)[i] || '').replace(/^"|"$/g, ''); return o; }, {}));
    let ok = 0, err = 0;
    for (const r of rows) {
      const email = (r.email || '').trim();
      if (!email) { err++; continue; }
      const display_name = (r.name || r.display_name || `${(r.first_name || r['first name'] || '').trim()} ${(r.last_name || r['last name'] || '').trim()}`.trim()).trim() || email;
      const row = {
        display_name: display_name || email,
        email,
        first_name: (r.first_name || r['first name'] || '').trim() || null,
        last_name: (r.last_name || r['last name'] || '').trim() || null,
        phone: (r.phone || '').trim() || null,
        employment_type: (r.employment_type || r.type || 'full_time').trim().toLowerCase() || 'full_time',
        start_date: (r.start_date || r['start date'] || '').trim() || null,
      };
      try { await apiClient.createPerson(row); ok++; } catch { err++; }
    }
    onDone?.();
    setToast(t('hr.toastImportDone', { ok, err }));
  };

  const refreshAllPeople = () => {
    if (useDemoData) { setAllPeople(DEMO_PEOPLE); return; }
    apiClient.getPeople({ limit: 100 }).then(r => setAllPeople(r.items || [])).catch(() => {});
  };

  const refreshAll = (msg) => {
    fetchPeople();
    refreshAllPeople();
    apiClient.getDepartments(false).then(setDepartments).catch(() => {});
    apiClient.getPositions(false).then(setPositions).catch(() => {});
    if (view === 'benefits' || view === 'directory') apiClient.getBenefitPlans(false).then(setBenefitPlans).catch(() => setBenefitPlans([]));
    if (view === 'pto') apiClient.getPTOPolicies(false).then(setPtoPolicies).catch(() => setPtoPolicies([]));
    if (view === 'overview') {
      apiClient.getUpcomingLeave().then(setUpcomingLeave).catch(() => setUpcomingLeave([]));
      apiClient.getAnnouncements().then(setAnnouncements).catch(() => setAnnouncements([]));
      if (localStorage.getItem('auth_token')) {
        apiClient.getMyTeam().then(r => setMyTeam(r.members || [])).catch(() => setMyTeam([]));
        apiClient.getMyNotifications().then(setNotifications).catch(() => setNotifications([]));
      }
    }
    if (msg) setToast(msg);
  };

  const activeDept = departments.find(d => d.id === activeDeptId);
  const activeDepts = departments.filter(d => d.is_active);
  const totalHeadcount = departments.reduce((s, d) => s + (d.people_count || 0), 0);

  const computedStats = {
    fullTime: people.filter(p => p.employment_type === 'full_time').length,
    partTime: people.filter(p => p.employment_type === 'part_time').length,
    intern: people.filter(p => p.employment_type === 'intern').length,
    onLeave: people.filter(p => (p.today_attendance_status === 'leave' || p.status === 'on_leave')).length,
  };

  /* ════════════════════════════════════════════════════ */
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-text tracking-tight">{t('hr.people')}</h1>
          <p className="text-gray-400 mt-1 text-sm">{totalHeadcount} {t('hr.teamMembers')} · {activeDepts.length} {t('hr.departments')} · {positions.filter(p => p.is_active).length} {t('hr.positions')}</p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {localStorage.getItem('auth_token') && (
            <div className="relative">
              <button onClick={() => setShowNotificationPanel(!showNotificationPanel)} className="p-2.5 text-gray-500 hover:text-text hover:bg-gray-100 rounded-lg transition-colors relative">
                <Bell className="h-5 w-5" />
                {notifications.filter(n => !n.read_at).length > 0 && (
                  <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full" />
                )}
              </button>
              {showNotificationPanel && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowNotificationPanel(false)} />
                  <div className="absolute right-0 top-full mt-1 w-80 max-h-96 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg z-50 py-2">
                    <p className="px-4 py-2 text-[11px] font-medium text-gray-500 uppercase">{t('hr.notifications')}</p>
                    {notifications.length === 0 ? (
                      <p className="px-4 py-6 text-gray-400 text-sm text-center">{t('hr.noNotifications')}</p>
                    ) : (
                      notifications.slice(0, 20).map(n => (
                        <button
                          key={n.id}
                          onClick={async () => { await apiClient.markNotificationRead(n.id); setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)); setShowNotificationPanel(false); }}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 ${!n.read_at ? 'bg-blue-50/30' : ''}`}
                        >
                          <p className="text-sm font-medium text-text">{n.title}</p>
                          {n.message && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>}
                          <p className="text-[11px] text-gray-400 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                        </button>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          <Link to="/hr/profile" className="flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-gray-600 hover:text-text border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <User className="h-4 w-4" /> {t('hr.myProfile')}
          </Link>
          <button onClick={() => setModal({ type: 'addPerson' })} className="flex items-center gap-1.5 px-4 py-2.5 bg-[#37352F] text-white rounded-lg text-sm font-medium hover:bg-[#4a4843] transition-colors shadow-sm">
            <UserPlus className="h-4 w-4" /> {t('hr.addPerson')}
          </button>
        </div>
      </div>

      {useDemoData && (
        <div className="mb-4 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center gap-2">
          <span className="text-amber-700">{t('hr.demoBanner')}</span>
        </div>
      )}

      {/* Nav tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-gray-200 overflow-x-auto">
        {[
          { id: 'overview', label: t('hr.overview'), icon: Building2 },
          { id: 'attendance', label: t('hr.attendance'), icon: Clock },
          { id: 'analytics', label: t('hr.analytics'), icon: BarChart3 },
          { id: 'directory', label: `${t('hr.directory')} (${totalPeople})`, icon: Users },
          { id: 'orgchart', label: t('hr.orgChart'), icon: Network },
          { id: 'reviews', label: t('hr.reviews'), icon: Award },
          { id: 'benefits', label: t('hr.benefits'), icon: Shield },
          { id: 'pto', label: t('hr.ptoPolicy'), icon: Plane },
          { id: 'positions', label: t('hr.positions'), icon: Briefcase },
          { id: 'audit', label: t('hr.auditLog'), icon: ClipboardList },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => { if (id === 'overview') goToOverview(); else if (id === 'directory') goToDirectory(); else if (id === 'analytics' || id === 'reviews') setView(id); else setView(id); }}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap -mb-px ${view === id ? 'border-[#37352F] text-text' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>

      {/* ══ OVERVIEW ══════════════════════════════════ */}
      {view === 'overview' && (
        <div className="space-y-8">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { label: t('hr.totalPeople'), value: totalHeadcount, accent: '#37352F' },
              { label: t('hr.fullTime'), value: computedStats.fullTime, accent: '#3B82F6' },
              { label: t('hr.partTime'), value: computedStats.partTime, accent: '#8B5CF6' },
              { label: t('hr.interns'), value: computedStats.intern, accent: '#F59E0B' },
              { label: t('hr.onLeaveToday'), value: computedStats.onLeave, accent: '#F43F5E' },
            ].map(({ label, value, accent }, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors">
                <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wide mb-1">{label}</p>
                <p className="text-2xl font-bold" style={{ color: accent }}>{value}</p>
              </div>
            ))}
          </div>

          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-text">{t('hr.departments')}</h2>
              <button onClick={() => setModal({ type: 'addDept' })} className="text-xs text-gray-400 hover:text-text font-medium transition-colors">+ {t('hr.addDepartment')}</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeDepts.map(d => {
                const pct = totalHeadcount > 0 ? Math.round((d.people_count / totalHeadcount) * 100) : 0;
                const deptPeople = allPeople.filter(p => p.department_id === d.id).slice(0, 4);
                return (
                  <div key={d.id} onClick={() => goToDept(d.id)} className="group bg-white border border-gray-200 rounded-xl p-5 cursor-pointer hover:border-gray-300 hover:shadow-sm transition-all">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-2.5 h-2.5 rounded-full mt-1 shrink-0" style={{ backgroundColor: d.color || '#9CA3AF' }} />
                        <div>
                          <h3 className="font-semibold text-text text-sm leading-tight">{d.name}</h3>
                          <p className="text-[11px] text-gray-400 mt-0.5">{d.code} · {d.people_count || 0} people</p>
                        </div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setModal({ type: 'editDept', data: d }); }} className="p-1 text-gray-300 hover:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Edit"><Edit className="h-3.5 w-3.5" /></button>
                    </div>
                    {d.description && <p className="text-xs text-gray-400 mb-3 line-clamp-1">{d.description}</p>}
                    {deptPeople.length > 0 && (
                      <div className="flex items-center mb-3">
                        <div className="flex -space-x-2">{deptPeople.map(p => <Avatar key={p.id} name={p.display_name} size="sm" photoUrl={p.profile_photo_url} />)}</div>
                        {(d.people_count || 0) > 4 && <span className="ml-2 text-[11px] text-gray-400">+{d.people_count - 4}</span>}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: d.color || '#9CA3AF' }} />
                      </div>
                      <span className="text-[11px] text-gray-400 w-8 text-right">{pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-text">{t('hr.teamMembers')}</h2>
              <button onClick={goToDirectory} className="text-xs text-gray-400 hover:text-text font-medium transition-colors">{t('hr.viewAll')} →</button>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-50 overflow-hidden">
              {people.slice(0, 10).map(p => (
                <div key={p.id} onClick={() => { setSelectedPerson(p); setView('directory'); }} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50/60 cursor-pointer transition-colors">
                  <Avatar name={p.display_name} size="sm" photoUrl={p.profile_photo_url} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text truncate">{p.display_name}</p>
                    <p className="text-[11px] text-gray-400 truncate">{p.position_title || t('hr.noPosition')} · {p.department_name || t('hr.unassigned')}</p>
                  </div>
                  <Badge className={statusCls((p.today_attendance_status === 'leave' ? 'on_leave' : p.status))}>{statusLabel((p.today_attendance_status === 'leave' ? 'on_leave' : p.status))}</Badge>
                  <Badge className="bg-gray-50 text-gray-500 ring-gray-200">{typeLabel(p.employment_type)}</Badge>
                </div>
              ))}
              {people.length === 0 && <p className="text-gray-400 text-center py-10 text-sm">{t('hr.noPeopleYet')}</p>}
            </div>
          </section>

          <section>
            <h2 className="text-base font-bold text-text mb-4">{t('hr.upcomingLeave')}</h2>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {Array.isArray(upcomingLeave) && upcomingLeave.length > 0 ? (
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-100 text-left text-[11px] font-medium text-gray-400 uppercase"><th className="px-5 py-3">Who</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Dates</th></tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {upcomingLeave.slice(0, 10).map(row => (
                      <tr key={row.id} className="hover:bg-gray-50/50">
                        <td className="px-5 py-3 font-medium text-text">{row.employee_name}</td>
                        <td className="px-4 py-3 text-gray-500">{row.leave_type}</td>
                        <td className="px-4 py-3 text-gray-500">{row.start_date && row.end_date ? `${new Date(row.start_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${new Date(row.end_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-gray-400 text-center py-8 text-sm">{t('hr.noUpcomingLeave')}</p>
              )}
            </div>
          </section>

          {Array.isArray(holidays) && holidays.length > 0 && (
            <section>
              <h2 className="text-base font-bold text-text mb-4">{t('hr.companyHolidays')}</h2>
              <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-50 overflow-hidden">
                {holidays.slice(0, 8).map(h => (
                  <div key={h.id} className="px-5 py-3 flex items-center justify-between text-sm">
                    <span className="font-medium text-text">{h.name}</span>
                    <span className="text-gray-500">{fmtDate(h.date)}{h.location ? ` · ${h.location}` : ''}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-text">{t('hr.announcements')}</h2>
              {localStorage.getItem('auth_token') && (
                <button onClick={() => setModal({ type: 'addAnnouncement' })} className="text-xs text-gray-400 hover:text-text font-medium transition-colors flex items-center gap-1"><Megaphone className="h-3.5 w-3.5" /> {t('hr.post')}</button>
              )}
            </div>
            <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-50 overflow-hidden">
              {announcements.length > 0 ? (
                announcements.slice(0, 5).map(a => (
                  <div key={a.id} className="px-5 py-4">
                    <p className="font-medium text-text text-sm">{a.title}</p>
                    {a.content && <p className="text-gray-500 text-sm mt-1 line-clamp-2">{a.content}</p>}
                    <p className="text-[11px] text-gray-400 mt-1">{a.author_name && `${a.author_name} · `}{a.created_at ? new Date(a.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</p>
                  </div>
                ))
              ) : (
                <p className="text-gray-400 text-center py-8 text-sm">{t('hr.noAnnouncements')}</p>
              )}
            </div>
          </section>

          {myTeam.length > 0 && (
            <section>
              <h2 className="text-base font-bold text-text mb-4">{t('hr.myTeam')}</h2>
              <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-50 overflow-hidden">
                {myTeam.slice(0, 8).map(m => (
                  <div key={m.id} onClick={() => { setSelectedPerson(m); setView('directory'); }} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50/60 cursor-pointer transition-colors">
                    <Avatar name={m.display_name} size="sm" photoUrl={m.profile_photo_url} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text truncate">{m.display_name}</p>
                      <p className="text-[11px] text-gray-400 truncate">{m.position_title || m.department_name || '—'}</p>
                    </div>
                    <Badge className={statusCls((m.today_attendance_status === 'leave' ? 'on_leave' : m.status))}>{statusLabel((m.today_attendance_status === 'leave' ? 'on_leave' : m.status))}</Badge>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* ══ Attendance (clock in/out, leave) ═══════════ */}
      {view === 'attendance' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h2 className="text-base font-bold text-text">{t('hr.attendance')}</h2>
            <div className="flex items-center gap-2">
              <input type="date" value={attendanceDate} onChange={e => setAttendanceDate(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              {localStorage.getItem('auth_token') && !useDemoData && (
                <>
                  <button onClick={async () => { setPunchLoading(true); try { await apiClient.punchIn(); setToast(t('hr.toastPunchedIn')); if (view === 'attendance') apiClient.getAttendance({ date: attendanceDate }).then(setAttendanceList).catch(() => {}); } catch { setToast(t('hr.toastPunchInFailed')); } setPunchLoading(false); }} disabled={punchLoading} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">{t('hr.clockIn')}</button>
                  <button onClick={async () => { setPunchLoading(true); try { await apiClient.punchOut(); setToast(t('hr.toastPunchedOut')); if (view === 'attendance') apiClient.getAttendance({ date: attendanceDate }).then(setAttendanceList).catch(() => {}); } catch { setToast(t('hr.toastPunchOutFailed')); } setPunchLoading(false); }} disabled={punchLoading} className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50">{t('hr.clockOut')}</button>
                </>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-500">{t('hr.attendanceDesc')}</p>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {attendanceLoading ? (
              <div className="p-12 text-center"><div className="inline-block h-6 w-6 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin" /></div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-100 text-left text-[11px] font-medium text-gray-400 uppercase"><th className="px-5 py-3">{t('hr.name')}</th><th className="px-4 py-3">{t('hr.status')}</th><th className="px-4 py-3">{t('hr.clockInCol')}</th><th className="px-4 py-3">{t('hr.clockOutCol')}</th></tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {attendanceList.map(row => (
                    <tr key={row.employee_id || row.id} className="hover:bg-gray-50/50">
                      <td className="px-5 py-3 font-medium text-text">{row.employee_name}</td>
                      <td className="px-4 py-3"><Badge className={ATTENDANCE_CLS[row.status] || ATTENDANCE_CLS.present}>{attLabel(row.status) || row.status}</Badge></td>
                      <td className="px-4 py-3 text-gray-500">{row.clock_in_at ? new Date(row.clock_in_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      <td className="px-4 py-3 text-gray-500">{row.clock_out_at ? new Date(row.clock_out_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!attendanceLoading && attendanceList.length === 0 && <p className="text-gray-400 text-center py-10 text-sm">{t('hr.noAttendanceData')}</p>}
          </div>
        </div>
      )}

      {/* ══ DIRECTORY ═════════════════════════════════ */}
      {view === 'directory' && (
        <div className="space-y-4">
          {activeDept && (
            <div className="flex items-center gap-2 text-sm">
              <button onClick={goToOverview} className="text-gray-400 hover:text-text transition-colors flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" />{t('hr.departments')}</button>
              <span className="text-gray-300">/</span>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: activeDept.color || '#9CA3AF' }} />
                <span className="font-medium text-text">{activeDept.name}</span>
              </div>
            </div>
          )}

          {/* Filters row */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
              <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder={t('hr.searchPlaceholder')} className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#37352F]/10 focus:border-gray-300 outline-none transition-colors" />
            </div>
            {!activeDeptId && (
              <FilterSelect value={activeDeptId || ''} onChange={v => setActiveDeptId(v || null)} options={[{ value: '', label: t('hr.allDepartments') }, ...activeDepts.map(d => ({ value: d.id, label: d.name }))]} />
            )}
            <FilterSelect value={statusFilter} onChange={setStatusFilter} options={[{ value: '', label: t('hr.allStatus') }, { value: 'active', label: t('hr.statusActive') }, { value: 'on_leave', label: t('hr.statusOnLeave') }, { value: 'offboarded', label: t('hr.statusOffboarded') }]} />
            <FilterSelect value={typeFilter} onChange={setTypeFilter} options={[{ value: '', label: t('hr.allTypes') }, ...Object.entries(TYPE_KEYS).map(([v, k]) => ({ value: v, label: t('hr.' + k) }))]} />
            <button onClick={() => setSort(s => s === 'name_asc' ? 'name_desc' : s === 'name_desc' ? 'start_date_desc' : 'name_asc')} className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-500 hover:border-gray-300 transition-colors" title="Toggle sort">
              <ArrowUpDown className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{sort === 'name_asc' ? 'A→Z' : sort === 'name_desc' ? 'Z→A' : 'Recent'}</span>
            </button>
            {/* CSV Export */}
            <button onClick={() => { exportCSV(people, departments, t, statusLabel, typeLabel); setToast(t('hr.toastCsvExported')); }} className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-500 hover:border-gray-300 transition-colors" title={t('hr.exportCSV')}>
              <Download className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t('hr.exportCSV')}</span>
            </button>
            {/* CSV Import */}
            <label className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-500 hover:border-gray-300 transition-colors cursor-pointer">
              <Upload className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{t('hr.importCSV')}</span>
              <input type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files?.[0] && handleCSVImport(e.target.files[0], refreshAll)} />
            </label>
          </div>

          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 p-3 bg-blue-50/50 border border-blue-100 rounded-lg">
              <span className="text-sm font-medium text-text">{selectedIds.size} {t('hr.selected')}</span>
              <button onClick={handleExportSelected} className="px-3 py-1.5 text-xs font-medium text-text bg-white border border-gray-200 rounded hover:bg-gray-50">{t('hr.export')}</button>
              <select onChange={e => { const v = e.target.value; if (v) { handleBulkStatus(v); e.target.value = ''; } }} className="px-3 py-1.5 text-xs font-medium text-text bg-white border border-gray-200 rounded hover:bg-gray-50">
                <option value="">{t('hr.changeStatus')}</option>
                <option value="active">{t('hr.statusActive')}</option>
                <option value="offboarded">{t('hr.offboard')}</option>
              </select>
              <button onClick={() => setSelectedIds(new Set())} className="px-3 py-1.5 text-xs text-gray-500 hover:text-text">{t('hr.clear')}</button>
            </div>
          )}

          <div className="flex gap-4">
            {/* Table */}
            <div className={`bg-white border border-gray-200 rounded-xl overflow-hidden flex-1 transition-all ${selectedPerson ? 'hidden lg:block' : ''}`}>
              {loading ? (
                <div className="p-16 text-center"><div className="inline-block h-6 w-6 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin" /></div>
              ) : people.length === 0 ? (
                <div className="p-16 text-center">
                  <Users className="h-10 w-10 mx-auto mb-3 text-gray-200" />
                  <p className="text-gray-400 text-sm">{t('hr.noPeopleFound')}</p>
                  <p className="text-gray-300 text-xs mt-1">{t('hr.tryAdjustFilters')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-100 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                      <th className="px-3 py-3 w-10">
                        <input type="checkbox" checked={people.length > 0 && selectedIds.size === people.length} onChange={toggleSelectAll} className="rounded border-gray-300" />
                      </th>
                      <th className="px-5 py-3">{t('hr.name')}</th>
                      <th className="px-4 py-3 hidden md:table-cell">{t('hr.department')}</th>
                      <th className="px-4 py-3 hidden lg:table-cell">{t('hr.position')}</th>
                      <th className="px-4 py-3 hidden sm:table-cell">{t('hr.employmentType')}</th>
                      <th className="px-4 py-3">{t('hr.status')}</th>
                      <th className="px-4 py-3 w-20"><span className="sr-only">Actions</span></th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {people.map(p => (
                        <tr key={p.id} onClick={() => setSelectedPerson(p)} className={`group cursor-pointer transition-colors ${selectedPerson?.id === p.id ? 'bg-blue-50/50' : 'hover:bg-gray-50/50'}`}>
                          <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                            <input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggleSelect(p.id)} className="rounded border-gray-300" />
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-3">
                              <Avatar name={p.display_name} size="sm" photoUrl={p.profile_photo_url} />
                              <div className="min-w-0">
                                <p className="font-medium text-text truncate">{p.display_name}</p>
                                <p className="text-[11px] text-gray-400 truncate">{p.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            {p.department_name ? (
                              <div className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: departments.find(d => d.id === p.department_id)?.color || '#9CA3AF' }} />
                                <span className="text-gray-600 text-[13px]">{p.department_name}</span>
                              </div>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-[13px] hidden lg:table-cell">{p.position_title || <span className="text-gray-300">—</span>}</td>
                          <td className="px-4 py-3 hidden sm:table-cell"><Badge className="bg-gray-50 text-gray-500 ring-gray-200">{typeLabel(p.employment_type)}</Badge></td>
                          <td className="px-4 py-3"><Badge className={statusCls((p.today_attendance_status === 'leave' ? 'on_leave' : p.status))}>{statusLabel((p.today_attendance_status === 'leave' ? 'on_leave' : p.status))}</Badge></td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={(e) => { e.stopPropagation(); setModal({ type: 'editPerson', data: p }); }} className="p-1.5 text-gray-400 hover:text-text rounded-md hover:bg-gray-100 transition-colors" title="Edit"><Edit className="h-3.5 w-3.5" /></button>
                              <button onClick={(e) => { e.stopPropagation(); handleDelete(p.id, p.display_name); }} className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors" title="Offboard"><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {pages > 1 && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 text-sm">
                  <span className="text-gray-400 text-xs">{t('hr.pageOf', { page, pages, total: totalPeople })}</span>
                  <div className="flex gap-1">
                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-1.5 border border-gray-200 rounded-lg disabled:opacity-30 hover:bg-gray-50 transition-colors"><ChevronLeft className="h-3.5 w-3.5 text-gray-500" /></button>
                    <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-1.5 border border-gray-200 rounded-lg disabled:opacity-30 hover:bg-gray-50 transition-colors"><ChevronRight className="h-3.5 w-3.5 text-gray-500" /></button>
                  </div>
                </div>
              )}
            </div>

            {/* Detail panel */}
            {selectedPerson && (
              <PersonPanel person={selectedPerson} departments={departments} allPeople={allPeople} positions={positions} benefitPlans={benefitPlans} onEdit={() => setModal({ type: 'editPerson', data: selectedPerson })} onDelete={() => handleDelete(selectedPerson.id, selectedPerson.display_name)} onClose={() => setSelectedPerson(null)} onRefresh={() => { fetchPeople(); refreshAllPeople(); }} setToast={setToast} t={t} statusLabel={statusLabel} typeLabel={typeLabel} />
            )}
          </div>
        </div>
      )}

      {/* ══ ANALYTICS ═════════════════════════════════ */}
      {view === 'analytics' && (
        <AnalyticsTab analytics={analytics} departments={departments} t={t} />
      )}

      {/* ══ ORG CHART ═════════════════════════════════ */}
      {view === 'orgchart' && (
        <OrgChart departments={activeDepts} people={allPeople} onPersonClick={(p) => { setSelectedPerson(p); setView('directory'); }} t={t} statusLabel={statusLabel} typeLabel={typeLabel} />
      )}

      {/* ══ REVIEWS ═════════════════════════════════ */}
      {view === 'reviews' && (
        <ReviewsTab useDemoData={useDemoData} reviewCycles={reviewCycles} allPeople={allPeople} setToast={setToast} onRefresh={() => (useDemoData ? setReviewCycles(DEMO_REVIEW_CYCLES) : apiClient.getReviewCycles().then(setReviewCycles))} t={t} />
      )}

      {/* ══ POSITIONS ═════════════════════════════════ */}
      {view === 'benefits' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-400">{benefitPlans.length} {t('hr.benefitPlans')}</p>
            <button onClick={() => setModal({ type: 'addBenefitPlan' })} className="text-xs text-gray-400 hover:text-text font-medium transition-colors">+ {t('hr.addPlan')}</button>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {benefitPlans.length === 0 ? (
              <div className="p-16 text-center">
                <Shield className="h-10 w-10 mx-auto mb-3 text-gray-200" />
                <p className="text-gray-400 text-sm">{t('hr.noBenefitPlans')}</p>
                <p className="text-gray-300 text-xs mt-1">{t('hr.noBenefitPlans')}</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-100 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                  <th className="px-5 py-3">{t('hr.name')}</th>
                  <th className="px-4 py-3">{t('hr.employmentType')}</th>
                  <th className="px-4 py-3 hidden sm:table-cell">{t('hr.description')}</th>
                  <th className="px-4 py-3">{t('hr.status')}</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {benefitPlans.map(plan => (
                    <tr key={plan.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3 font-medium text-text">{plan.name}</td>
                      <td className="px-4 py-3"><Badge className="bg-gray-50 text-gray-500 ring-gray-200">{plan.plan_type}</Badge></td>
                      <td className="px-4 py-3 text-gray-400 hidden sm:table-cell max-w-[200px] truncate">{plan.description || '—'}</td>
                      <td className="px-4 py-3"><Badge className={plan.is_active ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' : 'bg-gray-50 text-gray-400 ring-gray-200'}>{plan.is_active ? t('hr.active') : t('hr.inactive')}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
      {view === 'pto' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm text-gray-400">{ptoPolicies.length} {t('hr.ptoPolicies')}</p>
            <button onClick={() => setModal({ type: 'addPTOPolicy' })} className="text-xs text-gray-400 hover:text-text font-medium transition-colors">+ {t('hr.addPTOPolicy')}</button>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {ptoPolicies.length === 0 ? (
              <div className="p-16 text-center">
                <Plane className="h-10 w-10 mx-auto mb-3 text-gray-200" />
                <p className="text-gray-400 text-sm">{t('hr.noPtoPolicies')}</p>
                <p className="text-gray-300 text-xs mt-1">{t('hr.noPtoPolicies')}</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-100 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                  <th className="px-5 py-3">Name</th>
                  <th className="px-4 py-3">Hours/month</th>
                  <th className="px-4 py-3">Annual cap</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Status</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {ptoPolicies.map(pol => (
                    <tr key={pol.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3 font-medium text-text">{pol.name}</td>
                      <td className="px-4 py-3">{pol.hours_per_month}h</td>
                      <td className="px-4 py-3 text-gray-500">{pol.annual_cap_hours != null ? `${pol.annual_cap_hours}h` : '—'}</td>
                      <td className="px-4 py-3 text-gray-500">{pol.employment_type || 'All'}</td>
                      <td className="px-4 py-3"><Badge className={pol.is_active ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' : 'bg-gray-50 text-gray-400 ring-gray-200'}>{pol.is_active ? t('hr.active') : t('hr.inactive')}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="text-sm font-bold text-text mb-3">{t('hr.runAccrual')}</h3>
            <p className="text-xs text-gray-500 mb-3">Add accrual hours to all active employees for a given month (per their policy).</p>
            <div className="flex flex-wrap items-center gap-2">
              <select id="accrual-year" className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select id="accrual-month" className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{new Date(2000, m-1).toLocaleString(undefined, { month: 'long' })}</option>)}
              </select>
              <button onClick={async () => { const y = parseInt(document.getElementById('accrual-year').value, 10); const m = parseInt(document.getElementById('accrual-month').value, 10); const r = await apiClient.runPTOAccrual(y, m); setAccrualResult(r); setToast?.(t('hr.toastAccrualRun', { updated: r.updated, skipped: r.skipped })); }} className="px-4 py-2 text-sm font-medium bg-[#37352F] text-white rounded-lg hover:bg-[#4a4843]">{t('hr.runAccrual')}</button>
            </div>
            {accrualResult && <p className="text-sm text-gray-500 mt-2">Last run: {accrualResult.updated} updated, {accrualResult.skipped} skipped</p>}
          </div>
        </div>
      )}
      {view === 'positions' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-400">{positions.filter(p => p.is_active).length} {t('hr.active')} {t('hr.positions')}</p>
            <button onClick={() => setModal({ type: 'addPos' })} className="text-xs text-gray-400 hover:text-text font-medium transition-colors">+ {t('hr.addPosition')}</button>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-100 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                <th className="px-5 py-3">Title</th>
                <th className="px-4 py-3 hidden sm:table-cell">Code</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3 hidden md:table-cell">Department</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 w-12"></th>
              </tr></thead>
              <tbody className="divide-y divide-gray-50">
                {positions.map(p => {
                  const dept = departments.find(d => d.id === p.department_id);
                  return (
                    <tr key={p.id} className="hover:bg-gray-50/50 transition-colors group">
                      <td className="px-5 py-3"><div className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-gray-300 shrink-0" /><span className="font-medium text-text">{p.title}</span></div></td>
                      <td className="px-4 py-3 text-gray-400 font-mono text-[11px] hidden sm:table-cell">{p.code || '—'}</td>
                      <td className="px-4 py-3"><Badge className="bg-gray-50 text-gray-500 ring-gray-200">{typeLabel(p.employment_type)}</Badge></td>
                      <td className="px-4 py-3 hidden md:table-cell">{dept ? <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: dept.color || '#9CA3AF' }} /><span className="text-gray-500">{dept.name}</span></span> : <span className="text-gray-300">—</span>}</td>
                      <td className="px-4 py-3 text-center"><Badge className={p.is_active ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' : 'bg-gray-50 text-gray-400 ring-gray-200'}>{p.is_active ? t('hr.active') : t('hr.inactive')}</Badge></td>
                      <td className="px-4 py-3"><button onClick={() => setModal({ type: 'editPos', data: p })} className="p-1 text-gray-300 hover:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Edit"><Edit className="h-3.5 w-3.5" /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {positions.length === 0 && <p className="text-gray-400 text-center py-12 text-sm">{t('hr.noPositionsYet')}</p>}
          </div>
        </div>
      )}

      {/* ══ AUDIT ═════════════════════════════════════ */}
      {view === 'audit' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-400">{auditItems.length} {t('hr.auditEntries')}</p>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {auditItems.length === 0 ? (
              <div className="p-16 text-center">
                <ClipboardList className="h-10 w-10 mx-auto mb-3 text-gray-200" />
                <p className="text-gray-400 text-sm">{t('hr.noAuditEntries')}</p>
                <p className="text-gray-300 text-xs mt-1">Changes appear here when people are added, edited, or offboarded</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-100 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                  <th className="px-5 py-3">Time</th><th className="px-4 py-3">Employee</th><th className="px-4 py-3">Action</th><th className="px-4 py-3 hidden md:table-cell">Changed</th><th className="px-4 py-3 hidden sm:table-cell">By</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {auditItems.map(a => (
                    <tr key={a.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3 text-gray-400 whitespace-nowrap text-xs">{a.created_at ? new Date(a.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      <td className="px-4 py-3 font-medium text-text">{a.employee_name || '—'}</td>
                      <td className="px-4 py-3"><Badge className={a.action === 'delete' ? 'bg-red-50 text-red-700 ring-red-600/20' : a.action === 'create' ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' : 'bg-blue-50 text-blue-700 ring-blue-600/20'}>{a.action}</Badge></td>
                      <td className="px-4 py-3 text-gray-400 text-xs hidden md:table-cell">{a.changes?.fields?.join(', ') || '—'}</td>
                      <td className="px-4 py-3 text-gray-400 hidden sm:table-cell">{a.actor_name || 'System'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      {modal && <Modal modal={modal} setModal={setModal} departments={departments} positions={positions} allPeople={allPeople} onDone={refreshAll} t={t} statusLabel={statusLabel} typeLabel={typeLabel} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
};

/* ══════════════════════════════════════════════════════ */
/* ── Analytics Tab ────────────────────────────────────  */
const AnalyticsTab = ({ analytics, departments, t }) => {
  if (!analytics) return <div className="p-16 text-center"><div className="inline-block h-6 w-6 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin" /></div>;
  const maxDept = Math.max(...(analytics.headcount_by_department || []).map(d => d.count || 0), 1);
  const maxTenure = Math.max(...(analytics.tenure_buckets || []).map(t => t.count || 0), 1);
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-4"><p className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">{t('hr.active')}</p><p className="text-2xl font-bold text-text">{analytics.total_active}</p></div>
        <div className="bg-white border border-gray-200 rounded-xl p-4"><p className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">{t('hr.avgTenureMonths')}</p><p className="text-2xl font-bold text-blue-600">{analytics.avg_tenure_months}</p></div>
        <div className="bg-white border border-gray-200 rounded-xl p-4"><p className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">{t('hr.attrition12m')}</p><p className="text-2xl font-bold text-amber-600">{analytics.attrition_count}</p></div>
      </div>
      <section>
        <h2 className="text-base font-bold text-text mb-4">{t('hr.headcountByDept')}</h2>
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
          {(analytics.headcount_by_department || []).map((d, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-sm font-medium text-text w-32 truncate">{d.name}</span>
              <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
                <div className="h-full rounded bg-[#37352F] transition-all" style={{ width: `${(d.count / maxDept) * 100}%` }} />
              </div>
              <span className="text-sm text-gray-500 w-8 text-right">{d.count}</span>
            </div>
          ))}
        </div>
      </section>
      <section>
        <h2 className="text-base font-bold text-text mb-4">{t('hr.tenureDistribution')}</h2>
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
          {(analytics.tenure_buckets || []).map((t, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-sm font-medium text-text w-24">{t.bucket}</span>
              <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
                <div className="h-full rounded bg-blue-500/80 transition-all" style={{ width: `${(t.count / maxTenure) * 100}%` }} />
              </div>
              <span className="text-sm text-gray-500 w-8 text-right">{t.count}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

/* ══════════════════════════════════════════════════════ */
/* ── Reviews Tab ──────────────────────────────────────  */
const ReviewsTab = ({ useDemoData, reviewCycles, allPeople, setToast, onRefresh, t }) => {
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    if (!selectedCycle) { setReviews([]); return; }
    if (useDemoData) setReviews(getDemoReviewsForCycle(selectedCycle));
    else apiClient.getReviewsForCycle(selectedCycle).then(setReviews).catch(() => setReviews([]));
  }, [selectedCycle, useDemoData]);

  const handleAddReview = () => {
    if (!selectedCycle) return;
    const cycle = reviewCycles.find(c => c.id === selectedCycle);
    setModal({ type: 'addReview', cycle });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-400">{reviewCycles.length} {t('hr.reviewCycles')}</p>
        <button onClick={() => setModal({ type: 'addCycle' })} className="text-xs text-gray-400 hover:text-text font-medium transition-colors">+ {t('hr.addCycle')}</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <h3 className="px-5 py-3 border-b border-gray-100 font-semibold text-sm text-text">{t('hr.cycle')}</h3>
          <div className="divide-y divide-gray-50 max-h-[280px] overflow-y-auto">
            {reviewCycles.length === 0 ? <p className="p-5 text-gray-400 text-sm">{t('hr.noCyclesYet')}</p> : reviewCycles.map(c => (
              <button key={c.id} onClick={() => setSelectedCycle(c.id)} className={`w-full text-left px-5 py-3 hover:bg-gray-50 transition-colors flex items-center justify-between ${selectedCycle === c.id ? 'bg-blue-50/50' : ''}`}>
                <span className="font-medium text-sm text-text">{c.name}</span>
                <span className="text-[11px] text-gray-400">{c.review_count || 0} reviews</span>
              </button>
            ))}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <h3 className="px-5 py-3 border-b border-gray-100 font-semibold text-sm text-text flex items-center justify-between">
            <span>{t('hr.reviews')}</span>
            {selectedCycle && <button onClick={handleAddReview} className="text-xs text-text hover:underline">+ {t('hr.addReview')}</button>}
          </h3>
          <div className="divide-y divide-gray-50 max-h-[280px] overflow-y-auto">
            {reviews.length === 0 ? <p className="p-5 text-gray-400 text-sm">{selectedCycle ? t('hr.noReviewsInCycle') : t('hr.selectCycle')}</p> : reviews.map(r => (
              <ReviewRow key={r.id} review={r} onEdit={() => setModal({ type: 'editReview', review: r })} t={t} />
            ))}
          </div>
        </div>
      </div>
          {modal && <ReviewsModal modal={modal} setModal={setModal} reviewCycles={reviewCycles} allPeople={allPeople} onDone={() => { onRefresh(); setReviews([]); if (selectedCycle) apiClient.getReviewsForCycle(selectedCycle).then(setReviews); setToast?.(t('hr.toastSaved')); }} t={t} />}
    </div>
  );
};

const ReviewRow = ({ review, onEdit, t }) => {
  const ratingLabels = { meets_expectations: t('hr.meets'), exceeds: t('hr.exceeds'), below: t('hr.below') };
  const statusLabels = { draft: t('hr.draft'), self_submitted: t('hr.selfSubmitted'), manager_submitted: t('hr.managerSubmitted'), completed: t('hr.completed') };
  return (
    <div onClick={onEdit} className="px-5 py-3 hover:bg-gray-50 cursor-pointer flex items-center justify-between group">
      <div>
        <p className="text-sm font-medium text-text">{review.employee_name || '—'}</p>
        <p className="text-[11px] text-gray-400">{review.cycle_name} · {statusLabels[review.status] || review.status}</p>
      </div>
      <Badge className={review.rating ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' : 'bg-gray-50 text-gray-500 ring-gray-200'}>{ratingLabels[review.rating] || review.rating || t('hr.noRating')}</Badge>
    </div>
  );
};

const ReviewsModal = ({ modal, setModal, reviewCycles, allPeople, onDone, t }) => {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (modal?.type === 'addCycle') setForm({ name: '', start_date: '', end_date: '' });
    else if (modal?.type === 'addReview') setForm({ employee_id: '', cycle_id: modal.cycle?.id || '', manager_id: '' });
    else if (modal?.type === 'editReview') setForm({ ...modal.review });
  }, [modal]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (modal.type === 'addCycle') {
        await apiClient.createReviewCycle({ name: form.name || 'New Cycle', start_date: form.start_date || null, end_date: form.end_date || null });
      } else if (modal.type === 'addReview') {
        if (!form.employee_id || !form.cycle_id) { setSubmitting(false); return; }
        await apiClient.createPerformanceReview({ employee_id: form.employee_id, cycle_id: form.cycle_id, manager_id: form.manager_id || null });
      } else if (modal.type === 'editReview') {
        await apiClient.updatePerformanceReview(modal.review.id, { self_review: form.self_review, manager_feedback: form.manager_feedback, rating: form.rating, status: form.status });
      }
      setModal(null);
      onDone?.();
    } catch {}
    setSubmitting(false);
  };

  const isAddCycle = modal.type === 'addCycle';
  const isAddReview = modal.type === 'addReview';
  const isEditReview = modal.type === 'editReview';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25 backdrop-blur-[2px]" onClick={() => setModal(null)}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-text">{isAddCycle ? t('hr.addCycleTitle') : isAddReview ? t('hr.addReviewTitle') : t('hr.editReviewTitle')}</h2>
          <button onClick={() => setModal(null)} className="p-1 text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {isAddCycle && (
            <>
              <div><label className="block text-[11px] font-medium text-gray-500 mb-1.5 uppercase">{t('hr.name')}</label><input value={form.name || ''} onChange={e => set('name', e.target.value)} placeholder="Q1 2026" className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" /></div>
              <div className="grid grid-cols-2 gap-3"><div><label className="block text-[11px] font-medium text-gray-500 mb-1.5 uppercase">{t('hr.startDate')}</label><input type="date" value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" /></div><div><label className="block text-[11px] font-medium text-gray-500 mb-1.5 uppercase">{t('hr.endDate')}</label><input type="date" value={form.end_date || ''} onChange={e => set('end_date', e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" /></div></div>
            </>
          )}
          {isAddReview && (
            <>
              <div><label className="block text-[11px] font-medium text-gray-500 mb-1.5 uppercase">{t('hr.employee')}</label><select value={form.employee_id || ''} onChange={e => set('employee_id', e.target.value)} required className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"><option value="">—</option>{allPeople.filter(p => p.status !== 'offboarded').map(p => <option key={p.id} value={p.id}>{p.display_name}</option>)}</select></div>
              <div><label className="block text-[11px] font-medium text-gray-500 mb-1.5 uppercase">{t('hr.cycle')}</label><select value={form.cycle_id || modal.cycle?.id || ''} onChange={e => set('cycle_id', e.target.value)} required className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"><option value="">—</option>{reviewCycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
            </>
          )}
          {isEditReview && (
            <>
              <div><label className="block text-[11px] font-medium text-gray-500 mb-1.5 uppercase">{t('hr.selfReview')}</label><textarea value={form.self_review || ''} onChange={e => set('self_review', e.target.value)} rows={3} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm resize-none" /></div>
              <div><label className="block text-[11px] font-medium text-gray-500 mb-1.5 uppercase">{t('hr.managerFeedback')}</label><textarea value={form.manager_feedback || ''} onChange={e => set('manager_feedback', e.target.value)} rows={3} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm resize-none" /></div>
              <div><label className="block text-[11px] font-medium text-gray-500 mb-1.5 uppercase">{t('hr.rating')}</label><select value={form.rating || ''} onChange={e => set('rating', e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"><option value="">—</option><option value="meets_expectations">{t('hr.meets')}</option><option value="exceeds">{t('hr.exceeds')}</option><option value="below">{t('hr.below')}</option></select></div>
              <div><label className="block text-[11px] font-medium text-gray-500 mb-1.5 uppercase">{t('hr.status')}</label><select value={form.status || 'draft'} onChange={e => set('status', e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"><option value="draft">{t('hr.draft')}</option><option value="self_submitted">{t('hr.selfSubmitted')}</option><option value="manager_submitted">{t('hr.managerSubmitted')}</option><option value="completed">{t('hr.completed')}</option></select></div>
            </>
          )}
          <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
            <button type="button" onClick={() => setModal(null)} className="px-4 py-2 text-sm text-gray-500">{t('hr.cancel')}</button>
            <button type="submit" disabled={submitting} className="px-5 py-2 bg-[#37352F] text-white rounded-lg text-sm font-medium disabled:opacity-50 min-w-[80px]">{submitting ? <span className="inline-block h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : t('hr.save')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════ */
/* ── Org Chart ────────────────────────────────────────  */
const OrgChart = ({ departments, people, onPersonClick, t, statusLabel, typeLabel }) => {
  const [expandedDepts, setExpandedDepts] = useState(() => new Set(departments.map(d => d.id)));
  const [expandedManagers, setExpandedManagers] = useState(() => new Set());
  const activePeople = people.filter(p => p.status !== 'offboarded');

  const toggleDept = (id) => setExpandedDepts(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleManager = (id) => setExpandedManagers(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const deptGroups = useMemo(() => {
    return departments.map(dept => {
      const members = activePeople.filter(p => p.department_id === dept.id);
      const managerIds = new Set(members.filter(m => m.manager_id).map(m => m.manager_id));
      const managers = members.filter(m => managerIds.has(m.id));
      const unmanaged = members.filter(m => !m.manager_id && !managerIds.has(m.id));
      return { dept, managers, members, unmanaged };
    });
  }, [departments, activePeople]);

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-400">{departments.length} {t('hr.departments')} · {activePeople.length} {t('hr.active')} {t('hr.people')}</p>
      {deptGroups.map(({ dept, managers, members, unmanaged }) => {
        const isDeptExpanded = expandedDepts.has(dept.id);
        return (
          <div key={dept.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <button onClick={() => toggleDept(dept.id)} className="w-full px-5 py-3 border-b border-gray-100 flex items-center gap-2.5 hover:bg-gray-50/50 transition-colors text-left">
              <span className="text-gray-400">{isDeptExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}</span>
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: dept.color || '#9CA3AF' }} />
              <h3 className="font-semibold text-text text-sm">{dept.name}</h3>
              <span className="text-[11px] text-gray-400">{members.length} {t('hr.people')}</span>
            </button>
            {isDeptExpanded && (
              <div className="p-5">
                {members.length === 0 ? (
                  <p className="text-gray-300 text-sm text-center py-4">{t('hr.noPeopleYet')}</p>
                ) : (
                  <div className="space-y-4">
                    {managers.map(mgr => {
                      const reports = members.filter(m => m.manager_id === mgr.id);
                      const isMgrExpanded = expandedManagers.has(mgr.id) || reports.length === 0;
                      return (
                        <div key={mgr.id}>
                          <div className="flex items-center gap-1">
                            {reports.length > 0 ? (
                              <button onClick={() => toggleManager(mgr.id)} className="p-0.5 text-gray-400 hover:text-gray-600 shrink-0">
                                {isMgrExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
                              </button>
                            ) : <span className="w-4" />}
                            <OrgPersonCard person={mgr} isManager onClick={() => onPersonClick(mgr)} t={t} statusLabel={statusLabel} />
                          </div>
                          {reports.length > 0 && isMgrExpanded && (
                            <div className="ml-8 mt-1 pl-4 border-l-2 border-gray-100 space-y-1">
                              {reports.map(r => <OrgPersonCard key={r.id} person={r} onClick={() => onPersonClick(r)} t={t} statusLabel={statusLabel} />)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {unmanaged.length > 0 && (
                      <div className={managers.length > 0 ? 'mt-3 pt-3 border-t border-gray-100' : ''}>
                        {managers.length > 0 && <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-2">{t('hr.teamMembers')}</p>}
                        <div className="space-y-1">
                          {unmanaged.map(p => <OrgPersonCard key={p.id} person={p} onClick={() => onPersonClick(p)} t={t} statusLabel={statusLabel} />)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const OrgPersonCard = ({ person: p, isManager = false, onClick, t, statusLabel }) => (
  <div onClick={onClick} className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${isManager ? 'bg-gray-50/50' : ''}`}>
    <Avatar name={p.display_name} size="sm" photoUrl={p.profile_photo_url} />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5">
        <p className="text-sm font-medium text-text truncate">{p.display_name}</p>
        {isManager && <Badge className="bg-violet-50 text-violet-700 ring-violet-600/20">{t('hr.manager')}</Badge>}
      </div>
      <p className="text-[11px] text-gray-400 truncate">{p.position_title || t('hr.noPosition')}</p>
    </div>
    <Badge className={statusCls((p.today_attendance_status === 'leave' ? 'on_leave' : p.status))}>{statusLabel((p.today_attendance_status === 'leave' ? 'on_leave' : p.status))}</Badge>
  </div>
);

/* ══════════════════════════════════════════════════════ */
/* ── Person Detail Panel ──────────────────────────────  */
const PersonPanel = ({ person: p, departments, allPeople, positions, benefitPlans: benefitPlansProp, onEdit, onDelete, onClose, onRefresh, setToast, t, statusLabel, typeLabel }) => {
  const dept = departments.find(d => d.id === p.department_id);
  const manager = allPeople.find(m => m.id === p.manager_id);
  const directReports = allPeople.filter(m => m.manager_id === p.id && m.status !== 'offboarded');
  const [onboardingTasks, setOnboardingTasks] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [addTaskTitle, setAddTaskTitle] = useState('');
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ leave_type: 'vacation', start_date: '', end_date: '', notes: '' });
  const [benefitPlans, setBenefitPlans] = useState(benefitPlansProp || []);
  const [benefitEnrollments, setBenefitEnrollments] = useState([]);
  const [oneOnOneNotes, setOneOnOneNotes] = useState([]);
  const [showOneOnOneForm, setShowOneOnOneForm] = useState(false);
  const [oneOnOneForm, setOneOnOneForm] = useState({ note_date: new Date().toISOString().slice(0, 10), content: '' });
  const [enrollPlanId, setEnrollPlanId] = useState('');
  const [offboardingTasks, setOffboardingTasks] = useState([]);
  const [documentLinks, setDocumentLinks] = useState([]);
  const [offboardTaskTitle, setOffboardTaskTitle] = useState('');
  const [docLinkForm, setDocLinkForm] = useState({ title: '', url: '', category: '' });
  const [showDocForm, setShowDocForm] = useState(false);
  const [compHistory, setCompHistory] = useState([]);
  const [goals, setGoals] = useState([]);
  const [dependents, setDependents] = useState([]);
  const [ptoBalances, setPtoBalances] = useState([]);
  const [showCompForm, setShowCompForm] = useState(false);
  const [compForm, setCompForm] = useState({ effective_date: new Date().toISOString().slice(0, 10), salary: '', compensation_band: '', equity: '', change_reason: '' });
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [goalForm, setGoalForm] = useState({ title: '', description: '', target_value: '', current_value: '', due_date: '', status: 'not_started' });
  const [showDependentForm, setShowDependentForm] = useState(false);
  const [dependentForm, setDependentForm] = useState({ name: '', relationship: '', date_of_birth: '' });

  useEffect(() => {
    if (!benefitPlansProp?.length) apiClient.getBenefitPlans().then(setBenefitPlans).catch(() => setBenefitPlans([]));
  }, [benefitPlansProp]);
  useEffect(() => {
    apiClient.getOnboardingTasks(p.id).then(setOnboardingTasks).catch(() => setOnboardingTasks([]));
    apiClient.getLeaveRequests(p.id).then(setLeaveRequests).catch(() => setLeaveRequests([]));
    apiClient.getBenefitsForPerson(p.id).then(setBenefitEnrollments).catch(() => setBenefitEnrollments([]));
    apiClient.getOneOnOneNotes(p.id).then(setOneOnOneNotes).catch(() => setOneOnOneNotes([]));
    apiClient.getOffboardingTasks(p.id).then(setOffboardingTasks).catch(() => setOffboardingTasks([]));
    apiClient.getDocumentsForPerson(p.id).then(setDocumentLinks).catch(() => setDocumentLinks([]));
    apiClient.getCompensationHistory(p.id).then(setCompHistory).catch(() => setCompHistory([]));
    apiClient.getGoalsForPerson(p.id).then(setGoals).catch(() => setGoals([]));
    apiClient.getDependentsForPerson(p.id).then(setDependents).catch(() => setDependents([]));
    apiClient.getPTOBalancesForPerson(p.id).then(setPtoBalances).catch(() => setPtoBalances([]));
  }, [p.id]);

  const toggleTask = async (task) => {
    try {
      await apiClient.updateOnboardingTask(task.id, { completed: !task.completed });
      setOnboardingTasks(prev => prev.map(t => t.id === task.id ? { ...t, completed: !t.completed } : t));
      setToast?.('Task updated');
    } catch {}
  };

  const addTask = async () => {
    if (!addTaskTitle.trim()) return;
    try {
      const created = await apiClient.createOnboardingTask(p.id, { title: addTaskTitle.trim() });
      setOnboardingTasks(prev => [...prev, created]);
      setAddTaskTitle('');
      setToast?.('Task added');
    } catch {}
  };

  const submitLeave = async () => {
    if (!leaveForm.start_date || !leaveForm.end_date) { setToast?.('Fill start and end dates'); return; }
    try {
      await apiClient.createLeaveRequest(p.id, leaveForm);
      const list = await apiClient.getLeaveRequests(p.id);
      setLeaveRequests(list);
      setShowLeaveForm(false);
      setLeaveForm({ leave_type: 'vacation', start_date: '', end_date: '', notes: '' });
      setToast?.('Leave request submitted');
    } catch { setToast?.('Failed to submit'); }
  };

  const approveLeave = async (req) => {
    try { await apiClient.approveLeave(req.id); setLeaveRequests(prev => prev.map(r => r.id === req.id ? { ...r, status: 'approved' } : r)); setToast?.('Leave approved'); } catch {}
  };
  const denyLeave = async (req) => {
    try { await apiClient.denyLeave(req.id); setLeaveRequests(prev => prev.map(r => r.id === req.id ? { ...r, status: 'denied' } : r)); setToast?.('Leave denied'); } catch {}
  };

  const enrollBenefit = async () => {
    if (!enrollPlanId) return;
    try {
      await apiClient.enrollBenefit(p.id, { plan_id: enrollPlanId, status: 'enrolled' });
      const list = await apiClient.getBenefitsForPerson(p.id);
      setBenefitEnrollments(list);
      setEnrollPlanId('');
      setToast?.('Enrolled in plan');
    } catch { setToast?.('Enroll failed'); }
  };

  const addOneOnOneNote = async () => {
    if (!oneOnOneForm.content?.trim() || !oneOnOneForm.note_date) { setToast?.('Enter date and content'); return; }
    try {
      await apiClient.createOneOnOneNote(p.id, { note_date: oneOnOneForm.note_date, content: oneOnOneForm.content.trim() });
      const list = await apiClient.getOneOnOneNotes(p.id);
      setOneOnOneNotes(list);
      setShowOneOnOneForm(false);
      setOneOnOneForm({ note_date: new Date().toISOString().slice(0, 10), content: '' });
      setToast?.('Note added');
    } catch { setToast?.('Failed to add note'); }
  };

  const toggleOffboardingTask = async (task) => {
    try {
      await apiClient.updateOffboardingTask(task.id, { completed: !task.completed });
      const list = await apiClient.getOffboardingTasks(p.id);
      setOffboardingTasks(list);
      setToast?.('Task updated');
    } catch {}
  };
  const addOffboardingTask = async () => {
    if (!offboardTaskTitle.trim()) return;
    try {
      await apiClient.createOffboardingTask(p.id, { title: offboardTaskTitle.trim() });
      const list = await apiClient.getOffboardingTasks(p.id);
      setOffboardingTasks(list);
      setOffboardTaskTitle('');
      setToast?.('Task added');
    } catch {}
  };
  const addDocumentLink = async () => {
    if (!docLinkForm.title?.trim() || !docLinkForm.url?.trim()) { setToast?.('Title and URL required'); return; }
    try {
      await apiClient.createDocumentForPerson(p.id, { title: docLinkForm.title.trim(), url: docLinkForm.url.trim(), category: docLinkForm.category || undefined });
      const list = await apiClient.getDocumentsForPerson(p.id);
      setDocumentLinks(list);
      setDocLinkForm({ title: '', url: '', category: '' });
      setShowDocForm(false);
      setToast?.('Document link added');
    } catch { setToast?.('Failed to add'); }
  };
  const deleteDocumentLink = async (linkId) => {
    try {
      await apiClient.deleteDocumentForPerson(p.id, linkId);
      const list = await apiClient.getDocumentsForPerson(p.id);
      setDocumentLinks(list);
      setToast?.('Link removed');
    } catch {}
  };

  const addCompHistory = async () => {
    if (!compForm.effective_date) { setToast?.('Effective date required'); return; }
    try {
      await apiClient.createCompensationHistory(p.id, {
        effective_date: compForm.effective_date,
        salary: compForm.salary ? parseFloat(compForm.salary) : undefined,
        compensation_band: compForm.compensation_band || undefined,
        equity: compForm.equity || undefined,
        change_reason: compForm.change_reason || undefined,
      });
      const list = await apiClient.getCompensationHistory(p.id);
      setCompHistory(list);
      setShowCompForm(false);
      setCompForm({ effective_date: new Date().toISOString().slice(0, 10), salary: '', compensation_band: '', equity: '', change_reason: '' });
      setToast?.('Compensation record added');
    } catch { setToast?.('Failed'); }
  };
  const addGoal = async () => {
    if (!goalForm.title?.trim()) { setToast?.('Title required'); return; }
    try {
      await apiClient.createGoalForPerson(p.id, { ...goalForm, due_date: goalForm.due_date || undefined });
      const list = await apiClient.getGoalsForPerson(p.id);
      setGoals(list);
      setShowGoalForm(false);
      setGoalForm({ title: '', description: '', target_value: '', current_value: '', due_date: '', status: 'not_started' });
      setToast?.('Goal added');
    } catch { setToast?.('Failed'); }
  };
  const addDependent = async () => {
    if (!dependentForm.name?.trim()) { setToast?.('Name required'); return; }
    try {
      await apiClient.createDependentForPerson(p.id, { name: dependentForm.name.trim(), relationship: dependentForm.relationship || undefined, date_of_birth: dependentForm.date_of_birth || undefined });
      const list = await apiClient.getDependentsForPerson(p.id);
      setDependents(list);
      setShowDependentForm(false);
      setDependentForm({ name: '', relationship: '', date_of_birth: '' });
      setToast?.('Dependent added');
    } catch { setToast?.('Failed'); }
  };

  const ptoAccrued = p.pto_accrued_hours ?? 0;
  const ptoUsed = p.pto_used_hours ?? 0;
  const ptoRemaining = (p.pto_remaining_hours ?? Math.max(0, ptoAccrued - ptoUsed));
  const leaveTypes = getLeaveTypes(t);

  return (
    <div className="w-full lg:w-[340px] shrink-0 bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 px-5 pt-5 pb-4">
        <button onClick={onClose} className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 transition-colors"><X className="h-4 w-4" /></button>
        <div className="flex flex-col items-center text-center">
          <Avatar name={p.display_name} size="lg" photoUrl={p.profile_photo_url} />
          <h3 className="font-bold text-text mt-3 text-base">{p.display_name}</h3>
          <p className="text-xs text-gray-400 mt-0.5">{p.position_title || t('hr.noPosition')}</p>
          <div className="flex gap-1.5 mt-2">
            <Badge className={statusCls((p.today_attendance_status === 'leave' ? 'on_leave' : p.status))}>{statusLabel((p.today_attendance_status === 'leave' ? 'on_leave' : p.status))}</Badge>
            <Badge className="bg-gray-50 text-gray-500 ring-gray-200">{typeLabel(p.employment_type)}</Badge>
          </div>
        </div>
      </div>
      <div className="px-5 py-4 space-y-3 text-sm border-t border-gray-100">
        <DetailRow icon={Mail} label={t('hr.email')} value={p.email} href={`mailto:${p.email}`} />
        {p.phone && <DetailRow icon={Phone} label={t('hr.phone')} value={p.phone} href={`tel:${p.phone}`} />}
        <DetailRow icon={Building2} label={t('hr.department')} value={
          dept ? <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: dept.color || '#9CA3AF' }} />{p.department_name}</span> : (p.department_name || '—')
        } />
        <DetailRow icon={Briefcase} label={t('hr.position')} value={p.position_title || '—'} />
        {(p.compensation_band || p.salary || p.equity) && (
          <DetailRow icon={Award} label={t('hr.compensation')} value={
            [p.compensation_band, p.salary ? `$${Number(p.salary).toLocaleString()}` : null, p.equity].filter(Boolean).join(' · ') || '—'
          } />
        )}
        {p.start_date && <DetailRow icon={Calendar} label={t('hr.startDate')} value={fmtDate(p.start_date)} />}
        {p.end_date && <DetailRow icon={CalendarX} label={t('hr.endDate')} value={fmtDate(p.end_date)} />}
        {(manager || p.manager_name) && <DetailRow icon={Users} label={t('hr.manager')} value={manager?.display_name || p.manager_name} />}
        {p.notes && <DetailRow icon={FileText} label={t('hr.notes')} value={p.notes} />}
        {(p.emergency_contact_name || p.emergency_contact) && (
          <DetailRow icon={Phone} label={t('hr.emergencyContact')} value={
            p.emergency_contact_name
              ? [p.emergency_contact_name, p.emergency_contact_phone, p.emergency_contact_relationship].filter(Boolean).join(' · ')
              : p.emergency_contact
          } />
        )}
        {(ptoAccrued > 0 || ptoUsed > 0 || ptoBalances.length > 0) && (
          <DetailRow
            icon={Plane}
            label="PTO Balance"
            value={
              ptoBalances.length > 0 ? (
                <span className="space-y-0.5 block">
                  {ptoBalances.map(b => (
                    <span key={b.id} className="block text-xs">{leaveTypes[b.leave_type] || b.leave_type}: {Number(b.accrued_hours) - Number(b.used_hours)}h left</span>
                  ))}
                </span>
              ) : (
                <span>Accrued: {ptoAccrued}h · Used: {ptoUsed}h · <strong>Remaining: {ptoRemaining}h</strong></span>
              )
            }
          />
        )}
        {/* Onboarding checklist */}
        <div className="border-t border-gray-100 pt-3">
          <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><ListTodo className="h-3.5 w-3.5" /> {t('hr.onboardingTasks')} ({onboardingTasks.filter(x => x.completed).length}/{onboardingTasks.length})</p>
          <div className="space-y-1">
            {onboardingTasks.map(t => (
              <div key={t.id} className="flex items-center gap-2">
                <button onClick={() => toggleTask(t)} className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${t.completed ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300 hover:border-gray-400'}`}>{t.completed && <Check className="h-2.5 w-2.5" />}</button>
                <span className={`text-sm ${t.completed ? 'text-gray-400 line-through' : 'text-text'}`}>{t.title}</span>
                {t.due_date && <span className="text-[11px] text-gray-400 ml-auto">{fmtDate(t.due_date)}</span>}
              </div>
            ))}
          </div>
          <div className="flex gap-1 mt-2">
            <input value={addTaskTitle} onChange={e => setAddTaskTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTask()} placeholder="Add task..." className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-[#37352F]/20 outline-none" />
            <button onClick={addTask} className="px-2 py-1 text-xs font-medium text-text border border-gray-200 rounded hover:bg-gray-50">Add</button>
          </div>
        </div>
        {/* PTO / Leave */}
        <div className="border-t border-gray-100 pt-3">
          <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Plane className="h-3.5 w-3.5" /> {t('hr.leave')} ({leaveRequests.filter(r => r.status === 'pending').length} {t('hr.leavePending')})</p>
          {leaveRequests.length > 0 && (
            <div className="space-y-1 mb-2">
              {leaveRequests.slice(0, 5).map(r => (
                <div key={r.id} className="flex items-center justify-between text-sm">
                  <span className="text-text">{leaveTypes[r.leave_type] || r.leave_type} · {fmtDate(r.start_date)} – {fmtDate(r.end_date)}</span>
                  <div className="flex gap-0.5">
                    {r.status === 'pending' && (
                      <>
                        <button onClick={() => approveLeave(r)} className="text-emerald-600 hover:underline text-xs">Approve</button>
                        <button onClick={() => denyLeave(r)} className="text-red-600 hover:underline text-xs">Deny</button>
                      </>
                    )}
                    {r.status !== 'pending' && <Badge className={r.status === 'approved' ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' : 'bg-red-50 text-red-700 ring-red-600/20'}>{r.status}</Badge>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {!showLeaveForm ? (
            <button onClick={() => setShowLeaveForm(true)} className="text-xs text-text font-medium hover:underline">+ {t('hr.addLeave')}</button>
          ) : (
            <div className="space-y-2 p-2 bg-gray-50 rounded-lg">
              <select value={leaveForm.leave_type} onChange={e => setLeaveForm(f => ({ ...f, leave_type: e.target.value }))} className="w-full px-2 py-1 text-sm border border-gray-200 rounded">
                {Object.entries(leaveTypes).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input type="date" value={leaveForm.start_date} onChange={e => setLeaveForm(f => ({ ...f, start_date: e.target.value }))} className="w-full px-2 py-1 text-sm border border-gray-200 rounded" placeholder="Start" />
              <input type="date" value={leaveForm.end_date} onChange={e => setLeaveForm(f => ({ ...f, end_date: e.target.value }))} className="w-full px-2 py-1 text-sm border border-gray-200 rounded" placeholder="End" />
              <textarea value={leaveForm.notes} onChange={e => setLeaveForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes" rows={1} className="w-full px-2 py-1 text-sm border border-gray-200 rounded resize-none" />
              <div className="flex gap-1">
                <button onClick={submitLeave} className="px-2 py-1 text-xs font-medium bg-[#37352F] text-white rounded">{t('hr.save')}</button>
                <button onClick={() => setShowLeaveForm(false)} className="px-2 py-1 text-xs text-gray-500 hover:underline">{t('hr.cancel')}</button>
              </div>
            </div>
          )}
        </div>
        {/* Benefits */}
        <div className="border-t border-gray-100 pt-3">
          <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /> {t('hr.benefits')}</p>
          {benefitEnrollments.length > 0 && (
            <div className="space-y-1 mb-2">
              {benefitEnrollments.map(e => (
                <div key={e.id} className="flex items-center justify-between text-sm">
                  <span className="text-text">{e.plan_name || e.plan_type} ({e.status})</span>
                </div>
              ))}
            </div>
          )}
          {benefitPlans.length > 0 && (
            <div className="flex flex-wrap gap-1">
              <select value={enrollPlanId} onChange={e => setEnrollPlanId(e.target.value)} className="px-2 py-1 text-sm border border-gray-200 rounded">
                <option value="">—</option>
                {benefitPlans.filter(plan => !benefitEnrollments.some(e => e.plan_id === plan.id)).map(plan => (
                  <option key={plan.id} value={plan.id}>{plan.name}</option>
                ))}
              </select>
              <button onClick={enrollBenefit} disabled={!enrollPlanId} className="px-2 py-1 text-xs font-medium bg-[#37352F] text-white rounded disabled:opacity-50">{t('hr.add')}</button>
            </div>
          )}
        </div>
        {/* 1:1 Notes */}
        <div className="border-t border-gray-100 pt-3">
          <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> {t('hr.oneOnOneNotes')}</p>
          {oneOnOneNotes.length > 0 && (
            <div className="space-y-2 mb-2 max-h-32 overflow-y-auto">
              {oneOnOneNotes.slice(0, 10).map(n => (
                <div key={n.id} className="text-sm p-2 bg-gray-50 rounded">
                  <p className="text-[11px] text-gray-400">{fmtDate(n.note_date)}{n.author_name ? ` · ${n.author_name}` : ''}</p>
                  <p className="text-text whitespace-pre-wrap">{n.content}</p>
                </div>
              ))}
            </div>
          )}
          {!showOneOnOneForm ? (
            <button onClick={() => setShowOneOnOneForm(true)} className="text-xs text-text font-medium hover:underline">+ {t('hr.add')}</button>
          ) : (
            <div className="space-y-2 p-2 bg-gray-50 rounded-lg">
              <input type="date" value={oneOnOneForm.note_date} onChange={e => setOneOnOneForm(f => ({ ...f, note_date: e.target.value }))} className="w-full px-2 py-1 text-sm border border-gray-200 rounded" />
              <textarea value={oneOnOneForm.content} onChange={e => setOneOnOneForm(f => ({ ...f, content: e.target.value }))} placeholder="Note content..." rows={2} className="w-full px-2 py-1 text-sm border border-gray-200 rounded resize-none" />
              <div className="flex gap-1">
                <button onClick={addOneOnOneNote} className="px-2 py-1 text-xs font-medium bg-[#37352F] text-white rounded">Save</button>
                <button onClick={() => setShowOneOnOneForm(false)} className="px-2 py-1 text-xs text-gray-500 hover:underline">Cancel</button>
              </div>
            </div>
          )}
        </div>
        {/* Offboarding */}
        {(p.status === 'offboarded' || offboardingTasks.length > 0) && (
          <div className="border-t border-gray-100 pt-3">
            <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><ListTodo className="h-3.5 w-3.5" /> {t('hr.offboardingTasks')} ({offboardingTasks.filter(x => x.completed).length}/{offboardingTasks.length})</p>
            <div className="space-y-1 mb-2">
              {offboardingTasks.map(task => (
                <div key={task.id} className="flex items-center gap-2">
                  <button onClick={() => toggleOffboardingTask(task)} className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${task.completed ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300 hover:border-gray-400'}`}>{task.completed && <Check className="h-2.5 w-2.5" />}</button>
                  <span className={`text-sm ${task.completed ? 'text-gray-400 line-through' : 'text-text'}`}>{task.title}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-1">
              <input value={offboardTaskTitle} onChange={e => setOffboardTaskTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && addOffboardingTask()} placeholder="Add task..." className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-[#37352F]/20 outline-none" />
              <button onClick={addOffboardingTask} className="px-2 py-1 text-xs font-medium text-text border border-gray-200 rounded hover:bg-gray-50">Add</button>
            </div>
          </div>
        )}
        {/* Documents */}
        <div className="border-t border-gray-100 pt-3">
          <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> {t('hr.documents')}</p>
          {documentLinks.length > 0 && (
            <ul className="space-y-1 mb-2">
              {documentLinks.map(d => (
                <li key={d.id} className="flex items-center justify-between text-sm">
                  <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-text hover:underline truncate max-w-[180px]">{d.title}</a>
                  <button onClick={() => deleteDocumentLink(d.id)} className="text-red-600 hover:underline text-xs">Remove</button>
                </li>
              ))}
            </ul>
          )}
          {!showDocForm ? (
            <button onClick={() => setShowDocForm(true)} className="text-xs text-text font-medium hover:underline">+ {t('hr.add')}</button>
          ) : (
            <div className="space-y-2 p-2 bg-gray-50 rounded-lg">
              <input value={docLinkForm.title} onChange={e => setDocLinkForm(f => ({ ...f, title: e.target.value }))} placeholder="Title" className="w-full px-2 py-1 text-sm border border-gray-200 rounded" />
              <input value={docLinkForm.url} onChange={e => setDocLinkForm(f => ({ ...f, url: e.target.value }))} placeholder="URL" className="w-full px-2 py-1 text-sm border border-gray-200 rounded" />
              <div className="flex gap-1">
                <button onClick={addDocumentLink} className="px-2 py-1 text-xs font-medium bg-[#37352F] text-white rounded">Add</button>
                <button onClick={() => { setShowDocForm(false); setDocLinkForm({ title: '', url: '', category: '' }); }} className="px-2 py-1 text-xs text-gray-500 hover:underline">Cancel</button>
              </div>
            </div>
          )}
        </div>
        {/* Compensation history */}
        <div className="border-t border-gray-100 pt-3">
          <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Award className="h-3.5 w-3.5" /> {t('hr.compensationHistory')}</p>
          {compHistory.length > 0 && (
            <div className="space-y-1 mb-2">
              {compHistory.slice(0, 5).map(ch => (
                <div key={ch.id} className="text-sm text-text">
                  {fmtDate(ch.effective_date)} · {ch.salary != null ? `$${Number(ch.salary).toLocaleString()}` : ''} {ch.compensation_band || ''} {ch.change_reason ? `(${ch.change_reason})` : ''}
                </div>
              ))}
            </div>
          )}
          {!showCompForm ? (
            <button onClick={() => setShowCompForm(true)} className="text-xs text-text font-medium hover:underline">+ {t('hr.add')}</button>
          ) : (
            <div className="space-y-2 p-2 bg-gray-50 rounded-lg">
              <input type="date" value={compForm.effective_date} onChange={e => setCompForm(f => ({ ...f, effective_date: e.target.value }))} className="w-full px-2 py-1 text-sm border border-gray-200 rounded" />
              <input type="number" step="0.01" value={compForm.salary} onChange={e => setCompForm(f => ({ ...f, salary: e.target.value }))} placeholder="Salary" className="w-full px-2 py-1 text-sm border border-gray-200 rounded" />
              <input value={compForm.compensation_band} onChange={e => setCompForm(f => ({ ...f, compensation_band: e.target.value }))} placeholder="Band" className="w-full px-2 py-1 text-sm border border-gray-200 rounded" />
              <input value={compForm.change_reason} onChange={e => setCompForm(f => ({ ...f, change_reason: e.target.value }))} placeholder="Reason" className="w-full px-2 py-1 text-sm border border-gray-200 rounded" />
              <div className="flex gap-1">
                <button onClick={addCompHistory} className="px-2 py-1 text-xs font-medium bg-[#37352F] text-white rounded">Add</button>
                <button onClick={() => setShowCompForm(false)} className="px-2 py-1 text-xs text-gray-500 hover:underline">Cancel</button>
              </div>
            </div>
          )}
        </div>
        {/* Goals */}
        <div className="border-t border-gray-100 pt-3">
          <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Award className="h-3.5 w-3.5" /> {t('hr.goals')}</p>
          {goals.length > 0 && (
            <div className="space-y-1 mb-2">
              {goals.slice(0, 5).map(g => (
                <div key={g.id} className="text-sm text-text">{g.title} · <Badge className="bg-gray-50 text-gray-600 ring-gray-200">{g.status}</Badge></div>
              ))}
            </div>
          )}
          {!showGoalForm ? (
            <button onClick={() => setShowGoalForm(true)} className="text-xs text-text font-medium hover:underline">+ {t('hr.addGoal')}</button>
          ) : (
            <div className="space-y-2 p-2 bg-gray-50 rounded-lg">
              <input value={goalForm.title} onChange={e => setGoalForm(f => ({ ...f, title: e.target.value }))} placeholder="Title" className="w-full px-2 py-1 text-sm border border-gray-200 rounded" />
              <input type="date" value={goalForm.due_date} onChange={e => setGoalForm(f => ({ ...f, due_date: e.target.value }))} className="w-full px-2 py-1 text-sm border border-gray-200 rounded" />
              <select value={goalForm.status} onChange={e => setGoalForm(f => ({ ...f, status: e.target.value }))} className="w-full px-2 py-1 text-sm border border-gray-200 rounded">
                <option value="not_started">Not started</option>
                <option value="in_progress">In progress</option>
                <option value="completed">Completed</option>
              </select>
              <div className="flex gap-1">
                <button onClick={addGoal} className="px-2 py-1 text-xs font-medium bg-[#37352F] text-white rounded">Add</button>
                <button onClick={() => setShowGoalForm(false)} className="px-2 py-1 text-xs text-gray-500 hover:underline">Cancel</button>
              </div>
            </div>
          )}
        </div>
        {/* Dependents */}
        <div className="border-t border-gray-100 pt-3">
          <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {t('hr.dependents')}</p>
          {dependents.length > 0 && (
            <div className="space-y-1 mb-2">
              {dependents.map(d => (
                <div key={d.id} className="text-sm text-text">{d.name} {d.relationship ? `· ${d.relationship}` : ''} {d.date_of_birth ? fmtDate(d.date_of_birth) : ''}</div>
              ))}
            </div>
          )}
          {!showDependentForm ? (
            <button onClick={() => setShowDependentForm(true)} className="text-xs text-text font-medium hover:underline">+ {t('hr.add')}</button>
          ) : (
            <div className="space-y-2 p-2 bg-gray-50 rounded-lg">
              <input value={dependentForm.name} onChange={e => setDependentForm(f => ({ ...f, name: e.target.value }))} placeholder="Name" className="w-full px-2 py-1 text-sm border border-gray-200 rounded" />
              <input value={dependentForm.relationship} onChange={e => setDependentForm(f => ({ ...f, relationship: e.target.value }))} placeholder="Relationship" className="w-full px-2 py-1 text-sm border border-gray-200 rounded" />
              <input type="date" value={dependentForm.date_of_birth} onChange={e => setDependentForm(f => ({ ...f, date_of_birth: e.target.value }))} placeholder="DOB" className="w-full px-2 py-1 text-sm border border-gray-200 rounded" />
              <div className="flex gap-1">
                <button onClick={addDependent} className="px-2 py-1 text-xs font-medium bg-[#37352F] text-white rounded">Add</button>
                <button onClick={() => setShowDependentForm(false)} className="px-2 py-1 text-xs text-gray-500 hover:underline">Cancel</button>
              </div>
            </div>
          )}
        </div>
        {/* Direct reports */}
        {directReports.length > 0 && (
          <div>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-1.5">Direct Reports ({directReports.length})</p>
            <div className="space-y-1">
              {directReports.map(r => (
                <div key={r.id} className="flex items-center gap-2">
                  <Avatar name={r.display_name} size="sm" photoUrl={r.profile_photo_url} />
                  <span className="text-sm text-text truncate">{r.display_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="px-5 py-3 border-t border-gray-100 flex gap-2">
        <button onClick={onEdit} className="flex-1 px-3 py-2 text-sm font-medium bg-[#37352F] text-white rounded-lg hover:bg-[#4a4843] transition-colors">{t('hr.edit')}</button>
        <button onClick={onDelete} className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors">{t('hr.offboard')}</button>
      </div>
    </div>
  );
};

const DetailRow = ({ icon: Icon, label, value, href }) => (
  <div className="flex items-start gap-3">
    <Icon className="h-4 w-4 text-gray-300 mt-0.5 shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="text-[11px] text-gray-400 uppercase tracking-wide">{label}</p>
      {href ? <a href={href} className="text-sm text-text hover:underline truncate block">{value}</a> : <p className="text-sm text-text">{typeof value === 'string' ? value : value}</p>}
    </div>
  </div>
);

/* ── Filter select ────────────────────────────────────  */
const FilterSelect = ({ value, onChange, options }) => (
  <select value={value} onChange={e => onChange(e.target.value)} className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 hover:border-gray-300 transition-colors outline-none">
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

/* ══════════════════════════════════════════════════════ */
/* ── Modal ────────────────────────────────────────────  */
const Modal = ({ modal, setModal, departments, positions, allPeople, onDone, t, statusLabel, typeLabel }) => {
  const [form, setForm] = useState(modal.data || {});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const firstRef = useRef(null);

  useEffect(() => { firstRef.current?.focus(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError('');
    try {
      const labels = { addPerson: t('hr.toastSaved'), editPerson: t('hr.toastSaved'), addDept: t('hr.toastSaved'), editDept: t('hr.toastSaved'), addPos: t('hr.toastSaved'), editPos: t('hr.toastSaved'), addBenefitPlan: t('hr.toastSaved'), addAnnouncement: t('hr.toastSaved'), addPTOPolicy: t('hr.toastSaved') };
      if (modal.type === 'addPerson') await apiClient.createPerson(form);
      else if (modal.type === 'editPerson') await apiClient.updatePerson(modal.data.id, form);
      else if (modal.type === 'addDept') await apiClient.createDepartment(form);
      else if (modal.type === 'editDept') await apiClient.updateDepartment(modal.data.id, form);
      else if (modal.type === 'addPos') await apiClient.createPosition(form);
      else if (modal.type === 'editPos') await apiClient.updatePosition(modal.data.id, form);
      else if (modal.type === 'addBenefitPlan') await apiClient.createBenefitPlan(form);
      else if (modal.type === 'addAnnouncement') await apiClient.createAnnouncement(form);
      else if (modal.type === 'addPTOPolicy') await apiClient.createPTOPolicy({ name: form.name, hours_per_month: parseFloat(form.hours_per_month), annual_cap_hours: form.annual_cap_hours ? parseFloat(form.annual_cap_hours) : null, employment_type: form.employment_type || null });
      setModal(null);
      onDone(labels[modal.type] || 'Saved');
    } catch (err) { setError(err.message || 'Something went wrong'); }
    setSubmitting(false);
  };

  const isPerson = modal.type === 'addPerson' || modal.type === 'editPerson';
  const isDept = modal.type === 'addDept' || modal.type === 'editDept';
  const isPos = modal.type === 'addPos' || modal.type === 'editPos';
  const isBenefitPlan = modal.type === 'addBenefitPlan';
  const isAnnouncement = modal.type === 'addAnnouncement';
  const isPTOPolicy = modal.type === 'addPTOPolicy';
  const title = { addPerson: t('hr.addPersonTitle'), editPerson: t('hr.editPerson'), addDept: t('hr.addDepartmentTitle'), editDept: t('hr.editDepartment'), addPos: t('hr.addPosition'), editPos: t('hr.editPosition'), addBenefitPlan: t('hr.addBenefitPlan'), addAnnouncement: t('hr.post'), addPTOPolicy: t('hr.addPTOPolicy') }[modal.type];

  // Manager candidates: all active people except the person being edited
  const managerOptions = (allPeople || [])
    .filter(p => p.status !== 'offboarded' && p.id !== modal.data?.id)
    .map(p => ({ value: p.id, label: p.display_name }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25 backdrop-blur-[2px]" onClick={() => setModal(null)}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto animate-scale-in" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-text">{title}</h2>
          <button onClick={() => setModal(null)} className="p-1 text-gray-400 hover:text-gray-600 transition-colors"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <p className="text-red-600 text-sm bg-red-50 border border-red-100 p-3 rounded-lg">{error}</p>}

          {isPerson && (
            <>
              <Field ref={firstRef} label={t('hr.displayName')} value={form.display_name || ''} onChange={v => set('display_name', v)} required placeholder="e.g. Jane Smith" />
              <Field label={t('hr.email')} type="email" value={form.email || ''} onChange={v => set('email', v)} required placeholder="jane@example.com" />
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('hr.firstName')} value={form.first_name || ''} onChange={v => set('first_name', v)} />
                <Field label={t('hr.lastName')} value={form.last_name || ''} onChange={v => set('last_name', v)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <SelectField label={t('hr.department')} value={form.department_id || ''} onChange={v => set('department_id', v || null)} options={departments.filter(d => d.is_active).map(d => ({ value: d.id, label: d.name }))} />
                <SelectField label={t('hr.position')} value={form.position_id || ''} onChange={v => set('position_id', v || null)} options={positions.filter(p => p.is_active).map(p => ({ value: p.id, label: p.title }))} />
              </div>
              <SelectField label={t('hr.manager')} value={form.manager_id || ''} onChange={v => set('manager_id', v || null)} options={managerOptions} />
              <SelectField label={t('hr.employmentType')} value={form.employment_type || 'full_time'} onChange={v => set('employment_type', v)} options={Object.entries(TYPE_KEYS).map(([v, k]) => ({ value: v, label: t('hr.' + k) }))} />
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('hr.phone')} value={form.phone || ''} onChange={v => set('phone', v)} placeholder="(555) 123-4567" />
                <Field label={t('hr.startDate')} type="date" value={form.start_date || ''} onChange={v => set('start_date', v)} />
              </div>
              <div className="border-t border-gray-100 pt-3">
                <p className="text-[11px] font-medium text-gray-500 mb-1.5 uppercase tracking-wide">{t('hr.emergencyContact')}</p>
                <div className="grid grid-cols-1 gap-2 mt-1">
                  <Field label={t('hr.name')} value={form.emergency_contact_name || ''} onChange={v => set('emergency_contact_name', v)} placeholder="Contact name" />
                  <Field label={t('hr.phone')} value={form.emergency_contact_phone || ''} onChange={v => set('emergency_contact_phone', v)} placeholder="(555) 000-0000" />
                </div>
              </div>
              {modal.type === 'editPerson' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <SelectField label={t('hr.status')} value={form.status || 'active'} onChange={v => set('status', v)} options={[{ value: 'active', label: t('hr.statusActive') }, { value: 'offboarded', label: t('hr.statusOffboarded') }]} />
                    <Field label={t('hr.endDate')} type="date" value={form.end_date || ''} onChange={v => set('end_date', v)} />
                  </div>
                  <div className="grid grid-cols-2 gap-3 border-t border-gray-100 pt-3">
                    <Field label={t('hr.salary')} type="number" value={form.salary ?? ''} onChange={v => set('salary', v ? parseFloat(v) : null)} placeholder="e.g. 120000" />
                    <Field label={t('hr.compBand')} value={form.compensation_band || ''} onChange={v => set('compensation_band', v)} placeholder="e.g. IC3, L4" />
                    <div className="col-span-2"><Field label={t('hr.equity')} value={form.equity || ''} onChange={v => set('equity', v)} placeholder="e.g. 10,000 RSUs" /></div>
                    <Field label={t('hr.ptoAccrued')} type="number" value={form.pto_accrued_hours ?? ''} onChange={v => set('pto_accrued_hours', v ? parseFloat(v) : null)} placeholder="e.g. 120" min="0" step="0.5" />
                  </div>
                </>
              )}
              <TextareaField label={t('hr.notes')} value={form.notes || ''} onChange={v => set('notes', v)} placeholder={t('hr.internalNotes')} />
            </>
          )}

          {isDept && (
            <>
              <Field ref={firstRef} label={t('hr.name')} value={form.name || ''} onChange={v => set('name', v)} required placeholder="e.g. Engineering" />
              <Field label="Code" value={form.code || ''} onChange={v => set('code', v.toUpperCase())} required maxLength={20} placeholder="e.g. ENG" />
              <Field label="Description" value={form.description || ''} onChange={v => set('description', v)} placeholder="What this department does" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={form.color || '#3B82F6'} onChange={e => set('color', e.target.value)} className="w-8 h-8 rounded border border-gray-200 cursor-pointer" />
                    <input type="text" value={form.color || ''} onChange={e => set('color', e.target.value)} placeholder="#3B82F6" maxLength={7} className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#37352F]/10 focus:border-gray-300 focus:bg-white outline-none transition-all" />
                  </div>
                </div>
                <Field label="Display Order" type="number" value={form.display_order ?? 0} onChange={v => set('display_order', parseInt(v) || 0)} />
              </div>
              {modal.type === 'editDept' && (
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={form.is_active !== false} onChange={e => set('is_active', e.target.checked)} className="rounded border-gray-300" />
                  {t('hr.active')}
                </label>
              )}
            </>
          )}

          {isPos && (
            <>
              <Field ref={firstRef} label={t('hr.position')} value={form.title || ''} onChange={v => set('title', v)} required placeholder="e.g. Senior Engineer" />
              <Field label="Code" value={form.code || ''} onChange={v => set('code', v.toUpperCase())} maxLength={50} placeholder="e.g. ENG-SRE" />
              <SelectField label={t('hr.employmentType')} value={form.employment_type || 'full_time'} onChange={v => set('employment_type', v)} options={Object.entries(TYPE_KEYS).map(([v, k]) => ({ value: v, label: t('hr.' + k) }))} />
              <SelectField label={t('hr.department')} value={form.department_id || ''} onChange={v => set('department_id', v || null)} options={departments.filter(d => d.is_active).map(d => ({ value: d.id, label: d.name }))} />
              {modal.type === 'editPos' && (
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={form.is_active !== false} onChange={e => set('is_active', e.target.checked)} className="rounded border-gray-300" />
                  {t('hr.active')}
                </label>
              )}
            </>
          )}

          {isBenefitPlan && (
            <>
              <Field ref={firstRef} label="Plan Name" value={form.name || ''} onChange={v => set('name', v)} required placeholder="e.g. Health PPO" />
              <SelectField label="Type" value={form.plan_type || 'health'} onChange={v => set('plan_type', v)} options={[{ value: 'health', label: 'Health' }, { value: 'dental', label: 'Dental' }, { value: 'vision', label: 'Vision' }, { value: '401k', label: '401(k)' }, { value: 'other', label: 'Other' }]} />
              <div>
                <label className="block text-[11px] font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Description</label>
                <textarea value={form.description || ''} onChange={e => set('description', e.target.value)} placeholder="Optional description" rows={2} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Enrollment start (open enrollment)" type="date" value={form.enrollment_start_date || ''} onChange={v => set('enrollment_start_date', v)} />
                <Field label="Enrollment end" type="date" value={form.enrollment_end_date || ''} onChange={v => set('enrollment_end_date', v)} />
              </div>
            </>
          )}

          {isAnnouncement && (
            <>
              <Field ref={firstRef} label="Title" value={form.title || ''} onChange={v => set('title', v)} required placeholder="Announcement title" />
              <div>
                <label className="block text-[11px] font-medium text-gray-500 mb-1.5 uppercase tracking-wide">Content</label>
                <textarea value={form.content || ''} onChange={e => set('content', e.target.value)} rows={4} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm resize-none" placeholder="Optional message" />
              </div>
              <SelectField label="Audience" value={form.audience || 'all'} onChange={v => set('audience', v)} options={[{ value: 'all', label: 'Everyone' }, { value: 'department', label: 'My department only' }]} />
              {form.audience === 'department' && (
                <SelectField label="Department" value={form.department_id || ''} onChange={v => set('department_id', v || null)} options={(departments || []).filter(d => d.is_active).map(d => ({ value: d.id, label: d.name }))} />
              )}
            </>
          )}

          {isPTOPolicy && (
            <>
              <Field ref={firstRef} label="Policy name" value={form.name || ''} onChange={v => set('name', v)} required placeholder="e.g. Standard full-time" />
              <Field label="Hours per month" type="number" value={form.hours_per_month ?? ''} onChange={v => set('hours_per_month', v ? parseFloat(v) : null)} required placeholder="10" min="0" step="0.5" />
              <Field label="Annual cap (optional)" type="number" value={form.annual_cap_hours ?? ''} onChange={v => set('annual_cap_hours', v ? parseFloat(v) : null)} placeholder="120" min="0" step="0.5" />
              <SelectField label="Employment type (optional)" value={form.employment_type || ''} onChange={v => set('employment_type', v || null)} options={[{ value: '', label: 'All types' }, { value: 'full_time', label: 'Full-time' }, { value: 'part_time', label: 'Part-time' }, { value: 'intern', label: 'Intern' }, { value: 'contractor', label: 'Contractor' }]} />
            </>
          )}

          <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
            <button type="button" onClick={() => setModal(null)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">{t('hr.cancel')}</button>
            <button type="submit" disabled={submitting} className="px-5 py-2 bg-[#37352F] text-white rounded-lg text-sm font-medium hover:bg-[#4a4843] disabled:opacity-50 transition-colors min-w-[80px]">{submitting ? <span className="inline-block h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : t('hr.save')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Form helpers ─────────────────────────────────────── */
const Field = React.forwardRef(({ label, value, onChange, type = 'text', required, placeholder, maxLength }, ref) => (
  <div>
    <label className="block text-[11px] font-medium text-gray-500 mb-1.5 uppercase tracking-wide">{label}{required && <span className="text-red-400 ml-0.5">*</span>}</label>
    <input ref={ref} type={type} value={value} onChange={e => onChange(e.target.value)} required={required} placeholder={placeholder} maxLength={maxLength} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#37352F]/10 focus:border-gray-300 focus:bg-white outline-none transition-all placeholder:text-gray-300" />
  </div>
));
Field.displayName = 'Field';

const TextareaField = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="block text-[11px] font-medium text-gray-500 mb-1.5 uppercase tracking-wide">{label}</label>
    <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#37352F]/10 focus:border-gray-300 focus:bg-white outline-none transition-all placeholder:text-gray-300 resize-none" />
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div>
    <label className="block text-[11px] font-medium text-gray-500 mb-1.5 uppercase tracking-wide">{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#37352F]/10 focus:border-gray-300 focus:bg-white outline-none transition-all">
      <option value="">—</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

export default HR;
